/* ==========================================================================
   Ambient AI — conversational scent advisor
   - Talks like a real advisor: greets, asks the right questions, explains picks
   - Reads the whole catalogue (every product + description) once, cached
   - Understands notes, moods, budget, gender, brand, category, season,
     occasion, longevity, "smells like…", gifts and follow-ups
   - Suggests bundles, layering partners and free-delivery top-ups
   - Answers delivery / returns / authenticity / payment questions
   - Optional: forwards matches to a language-model endpoint for wording
   ========================================================================== */
(function (root) {
  'use strict';

  const NOTES = {
    // sweet / gourmand
    vanilla: ['vanilla', 'vanille', 'bourbon vanilla', 'madagascar vanilla'],
    caramel: ['caramel', 'toffee', 'butterscotch', 'dulce de leche'],
    tonka: ['tonka', 'tonka bean', 'coumarin'],
    praline: ['praline', 'praliné', 'nougat'],
    honey: ['honey', 'honeyed'],
    chocolate: ['chocolate', 'cocoa', 'cacao'],
    coffee: ['coffee', 'espresso', 'mocha'],
    sugar: ['sugar', 'brown sugar', 'candy', 'candied', 'cotton candy', 'candyfloss', 'marshmallow', 'bubblegum', 'meringue'],
    almond: ['almond', 'marzipan', 'heliotrope'],
    pistachio: ['pistachio'],
    milk: ['milk', 'milky', 'rice milk'],
    // floral
    rose: ['rose', 'damask rose', 'turkish rose', 'taif rose', 'bulgarian rose', 'rose absolute'],
    jasmine: ['jasmine', 'jasmin', 'sambac'],
    tuberose: ['tuberose'],
    peony: ['peony'],
    lily: ['lily', 'lily of the valley', 'muguet'],
    'orange blossom': ['orange blossom', 'orange flower', 'neroli'],
    iris: ['iris', 'orris'],
    violet: ['violet'],
    ylang: ['ylang', 'ylang-ylang', 'ylang ylang'],
    gardenia: ['gardenia'],
    magnolia: ['magnolia'],
    freesia: ['freesia'],
    lavender: ['lavender', 'lavandin'],
    orchid: ['orchid'],
    osmanthus: ['osmanthus'],
    geranium: ['geranium'],
    narcissus: ['narcissus', 'daffodil'],
    'cherry blossom': ['cherry blossom', 'sakura'],
    // woody
    oud: ['oud', 'agarwood', 'oudh', 'aoud', 'oud wood'],
    sandalwood: ['sandalwood', 'sandal'],
    cedar: ['cedar', 'cedarwood'],
    vetiver: ['vetiver'],
    patchouli: ['patchouli'],
    guaiac: ['guaiac', 'guaiacwood'],
    cashmere: ['cashmere wood', 'cashmeran', 'cashmere'],
    pine: ['pine', 'fir', 'cypress', 'juniper'],
    birch: ['birch'],
    oakmoss: ['oakmoss', 'moss'],
    // amber / spice / resins
    amber: ['amber', 'ambery', 'ambergris', 'ambroxan', 'ambrox'],
    saffron: ['saffron'],
    incense: ['incense', 'frankincense', 'olibanum', 'myrrh'],
    benzoin: ['benzoin', 'labdanum', 'resin', 'resins', 'styrax'],
    cinnamon: ['cinnamon'],
    cardamom: ['cardamom'],
    pepper: ['pepper', 'pink pepper', 'black pepper'],
    clove: ['clove', 'nutmeg'],
    ginger: ['ginger'],
    tobacco: ['tobacco'],
    leather: ['leather', 'suede'],
    smoke: ['smoke', 'smoky', 'smokey', 'birch tar'],
    rum: ['rum', 'cognac', 'whisky', 'whiskey', 'bourbon', 'liquor', 'boozy'],
    // fresh / citrus
    bergamot: ['bergamot'],
    lemon: ['lemon', 'lime', 'citron', 'yuzu'],
    orange: ['orange', 'blood orange', 'mandarin', 'tangerine', 'clementine'],
    grapefruit: ['grapefruit'],
    citrus: ['citrus', 'citrusy', 'citrussy', 'zesty'],
    mint: ['mint', 'peppermint', 'spearmint'],
    marine: ['marine', 'aquatic', 'sea notes', 'sea salt', 'ocean', 'ozonic', 'sea breeze', 'salt'],
    tea: ['tea', 'green tea', 'black tea', 'matcha'],
    green: ['green notes', 'grass', 'violet leaf', 'galbanum', 'fig leaf'],
    basil: ['basil', 'sage', 'clary sage', 'rosemary', 'thyme'],
    // fruity
    apple: ['apple'],
    pear: ['pear'],
    peach: ['peach', 'apricot', 'nectarine'],
    plum: ['plum', 'prune'],
    cherry: ['cherry'],
    berries: ['berry', 'berries', 'raspberry', 'strawberry', 'blackberry', 'blueberry', 'blackcurrant', 'cassis', 'redcurrant'],
    pineapple: ['pineapple'],
    mango: ['mango'],
    lychee: ['lychee', 'litchi'],
    coconut: ['coconut'],
    fig: ['fig'],
    pomegranate: ['pomegranate'],
    melon: ['melon', 'watermelon'],
    'passion fruit': ['passion fruit', 'passionfruit'],
    // musky / clean
    musk: ['musk', 'musky', 'white musk', 'ambrette'],
    powder: ['powder', 'powdery', 'talc'],
    aldehydes: ['aldehyde', 'aldehydes', 'soapy', 'soap', 'clean linen', 'cotton']
  };

  const FAMILIES = {
    sweet: { words: ['sweet', 'gourmand', 'sugary', 'dessert', 'edible', 'yummy', 'delicious'], notes: ['vanilla', 'caramel', 'tonka', 'praline', 'honey', 'chocolate', 'sugar', 'almond', 'coffee', 'milk'], label: 'sweet, gourmand' },
    floral: { words: ['floral', 'flowery', 'flower', 'flowers', 'bouquet', 'feminine', 'romantic'], notes: ['rose', 'jasmine', 'tuberose', 'peony', 'orange blossom', 'iris', 'ylang', 'gardenia', 'lily', 'freesia', 'magnolia'], label: 'floral' },
    woody: { words: ['woody', 'woods', 'earthy', 'wood'], notes: ['oud', 'sandalwood', 'cedar', 'vetiver', 'patchouli', 'guaiac', 'cashmere'], label: 'woody' },
    fresh: { words: ['fresh', 'clean', 'light', 'airy', 'aquatic', 'summery', 'refreshing', 'crisp', 'sporty'], notes: ['bergamot', 'lemon', 'orange', 'grapefruit', 'citrus', 'mint', 'marine', 'tea', 'green'], label: 'fresh' },
    amber: { words: ['spicy', 'warm', 'oriental', 'cosy', 'cozy', 'amber', 'ambery', 'rich', 'exotic', 'arabian', 'middle eastern'], notes: ['amber', 'saffron', 'incense', 'benzoin', 'cinnamon', 'cardamom', 'pepper', 'oud'], label: 'warm & spicy' },
    fruity: { words: ['fruity', 'juicy', 'fruit', 'tropical'], notes: ['apple', 'pear', 'peach', 'plum', 'cherry', 'berries', 'pineapple', 'mango', 'lychee', 'coconut', 'fig', 'passion fruit'], label: 'fruity' },
    musky: { words: ['musky', 'skin scent', 'powdery', 'soft', 'soapy', 'cosy clean', 'just showered'], notes: ['musk', 'powder', 'aldehydes', 'cashmere'], label: 'soft & musky' },
    dark: { words: ['dark', 'smoky', 'smokey', 'leathery', 'mysterious', 'intense', 'bold', 'masculine', 'boozy'], notes: ['leather', 'tobacco', 'smoke', 'oud', 'incense', 'rum', 'birch'], label: 'dark & smoky' }
  };

  const GENDER = {
    women: ['women', 'woman', 'womens', "women's", 'her', 'she', 'female', 'ladies', 'lady', 'girl', 'girls', 'girlfriend', 'wife', 'mum', 'mom', 'mother', 'sister', 'daughter', 'feminine', 'fiancee', 'fiancée', 'auntie', 'grandma', 'nan'],
    men: ['men', 'man', 'mens', "men's", 'him', 'he', 'male', 'gents', 'gentleman', 'boy', 'boys', 'boyfriend', 'husband', 'dad', 'father', 'brother', 'son', 'masculine', 'fiance', 'fiancé', 'uncle', 'grandad', 'guy'],
    unisex: ['unisex', 'gender neutral', 'genderless', 'shared', 'both', 'couple', 'anyone']
  };

  const CATEGORIES = {
    fragrance: { words: ['perfume', 'perfumes', 'fragrance', 'fragrances', 'scent', 'scents', 'cologne', 'edp', 'edt', 'eau de parfum', 'eau de toilette', 'parfum', 'extrait', 'attar', 'perfume oil'], match: ['fragrance', 'parfum', 'toilette', 'cologne', 'perfume', 'extrait', 'attar'], coll: 'fragrance' },
    bodyspray: { words: ['body spray', 'body mist', 'mist', 'deodorant spray', 'body sprays'], match: ['body spray', 'body mist', 'mist', 'deodorant'], coll: 'bath' },
    gift: { words: ['gift set', 'gift sets', 'giftset', 'bundle', 'hamper', 'box set', 'coffret'], match: ['gift set', 'set', 'gift'], coll: 'gift' },
    skincare: { words: ['skincare', 'skin care', 'serum', 'moisturiser', 'moisturizer', 'cream', 'face cream', 'cleanser', 'toner', 'face', 'skin', 'spf', 'sunscreen', 'eye cream', 'night cream', 'lotion', 'anti-ageing', 'anti-aging', 'wrinkles', 'acne', 'hydrating', 'dry skin', 'oily skin'], match: ['serum', 'cream', 'moistur', 'cleans', 'toner', 'lotion', 'skin', 'face', 'spf', 'eye'], coll: 'skincare' },
    haircare: { words: ['hair', 'haircare', 'hair care', 'shampoo', 'conditioner', 'hair mask', 'hair oil', 'frizz', 'curly', 'damaged hair', 'colour treated', 'color treated'], match: ['shampoo', 'conditioner', 'hair', 'masque', 'mask', 'bain'], coll: 'haircare' },
    makeup: { words: ['makeup', 'make up', 'make-up', 'lipstick', 'lip gloss', 'lip', 'mascara', 'foundation', 'concealer', 'eyeliner', 'eyeshadow', 'palette', 'blush', 'bronzer', 'primer', 'highlighter', 'brush', 'brushes', 'powder foundation'], match: ['lip', 'mascara', 'foundation', 'concealer', 'liner', 'shadow', 'palette', 'blush', 'bronzer', 'primer', 'highlight', 'brush', 'makeup', 'make-up'], coll: 'makeup' },
    bath: { words: ['bath', 'body', 'shower gel', 'body wash', 'body lotion', 'body butter', 'deodorant', 'hand cream', 'soap'], match: ['shower', 'wash', 'body lotion', 'body butter', 'deodorant', 'hand cream', 'soap', 'bath'], coll: 'bath' }
  };

  const SEASONS = {
    summer: ['summer', 'summery', 'hot weather', 'holiday', 'vacation', 'beach', 'heat', 'warm weather'],
    spring: ['spring', 'springtime'],
    autumn: ['autumn', 'fall', 'autumnal'],
    winter: ['winter', 'cold weather', 'christmas', 'festive', 'chilly', 'cold']
  };
  const SEASON_BIAS = { summer: ['fresh', 'fruity'], spring: ['floral', 'fresh'], autumn: ['woody', 'amber'], winter: ['amber', 'sweet', 'dark'] };

  const OCCASIONS = {
    office: { words: ['office', 'work', 'workplace', 'daily', 'everyday', 'every day', 'daytime', 'day time', 'casual', 'school', 'uni', 'signature'], desc: ['office', 'daily', 'everyday', 'day', 'casual', 'versatile', 'work'] },
    evening: { words: ['evening', 'night', 'night out', 'date', 'date night', 'romantic', 'seductive', 'sexy', 'clubbing', 'dinner'], desc: ['evening', 'night', 'date', 'romantic', 'seductive', 'sensual'] },
    special: { words: ['wedding', 'special occasion', 'occasion', 'party', 'formal', 'event', 'eid', 'celebration', 'gala'], desc: ['special', 'occasion', 'wedding', 'party', 'formal', 'celebration', 'luxur'] },
    gym: { words: ['gym', 'sport', 'sports', 'workout', 'active'], desc: ['sport', 'fresh', 'energ', 'active'] }
  };

  const PERFORMANCE = ['long lasting', 'long-lasting', 'longlasting', 'lasts long', 'last long', 'last all day', 'all day', 'longevity', 'beast mode', 'beast', 'strong', 'powerful', 'projection', 'projects', 'sillage', 'compliment', 'compliments', 'performance', 'potent'];
  const PERF_DESC = ['long-lasting', 'long lasting', 'longevity', 'all day', 'hours', 'lasting', 'projection', 'sillage', 'powerful', 'intense', 'compliment'];

  const CONCENTRATION = {
    'extrait': ['extrait', 'extrait de parfum', 'pure parfum'],
    'eau de parfum': ['edp', 'eau de parfum'],
    'eau de toilette': ['edt', 'eau de toilette'],
    'parfum': ['parfum'],
    'oil': ['perfume oil', 'attar', 'oil', 'concentrated oil']
  };

  const BRAND_ALIASES = { ysl: 'yves saint laurent', loreal: "l'oréal", "l'oreal": "l'oréal", 'estee': 'estée lauder', 'estee lauder': 'estée lauder', kerastase: 'kérastase', kiehls: "kiehl's", 'al haramain': 'al haramain', 'maison alhambra': 'maison alhambra', mancera: 'mancera', creed: 'creed' };

  const STOP = new Set('a an and are about any anything around at be best can could do does find for from get give good great have help i i\'d id im i\'m in is it its it\'s just like looking love me more my need nice of on one or please recommend show smell smells some something suggest that the them then these this those to try under up want was we what whats what\'s which who will with would you your perfume perfumes fragrance fragrances scent scents product products gift gifts buy price cost cheap cheaper budget pounds £ quid notes note option options also maybe really very much bit little'.split(' '));

  const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[’`]/g, "'");
  const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const RE_CACHE = new Map();
  const hasWord = (text, w) => { let r = RE_CACHE.get(w); if (!r) { r = new RegExp('(^|[^a-z])' + escapeRe(w) + '($|[^a-z])'); RE_CACHE.set(w, r); } return r.test(text); };
  const cap = (s) => String(s).split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  const uniq = (a) => Array.from(new Set(a));
  const listify = (arr, conj = 'and') => arr.length <= 1 ? (arr[0] || '') : arr.slice(0, -1).join(', ') + ' ' + conj + ' ' + arr[arr.length - 1];
  const pick = (arr, seed) => arr[Math.abs(seed) % arr.length];
  const hash = (s) => { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0; return h; };

  /* ------------------------------------------------------------------ */
  /* Intent parsing                                                     */
  /* ------------------------------------------------------------------ */
  function parseIntent(raw, ctx) {
    ctx = ctx || {};
    const vendors = ctx.vendors || [];
    let t = ' ' + norm(raw).replace(/[^\w£$€'.\-\s&]/g, ' ').replace(/\s+/g, ' ') + ' ';
    const intent = { raw: raw, notes: [], families: [], gender: null, min: null, max: null, around: null, brands: [], category: null, seasons: [], occasions: [], performance: false, concentration: null, size: null, similar: null, sale: false, inStock: false, luxury: false, keywords: [], refine: {} };

    // sizes first (so "100ml" isn't read as a price)
    const size = t.match(/(\d{1,4})\s?ml\b/);
    if (size) { intent.size = parseInt(size[1], 10); t = t.replace(/(\d{1,4})\s?ml\b/g, ' '); }

    // budget
    const num = '£?\\s?(\\d+(?:\\.\\d{1,2})?)\\s?(?:pounds|quid|gbp|£)?';
    let m;
    if ((m = t.match(new RegExp('between\\s' + num + '\\s(?:and|-|to)\\s' + num)))) { intent.min = +m[1]; intent.max = +m[2]; }
    else if ((m = t.match(new RegExp('£\\s?(\\d+(?:\\.\\d{1,2})?)\\s?(?:-|–|to)\\s?£?\\s?(\\d+(?:\\.\\d{1,2})?)')))) { intent.min = +m[1]; intent.max = +m[2]; }
    else {
      if ((m = t.match(new RegExp('(?:under|below|less than|max|maximum|up to|upto|within|no more than|not more than|cheaper than|less then|<|at most|budget of|budget is|budget)\\s' + num)))) intent.max = +m[1];
      if ((m = t.match(new RegExp('(?:over|above|more than|at least|minimum|min|>|from)\\s' + num)))) intent.min = +m[1];
      if ((m = t.match(new RegExp('(?:around|about|roughly|approx|approximately|circa|near|close to|~)\\s' + num)))) intent.around = +m[1];
      if (intent.max == null && intent.min == null && intent.around == null && (m = t.match(/£\s?(\d+(?:\.\d{1,2})?)/))) intent.max = +m[1];
      if (intent.max == null && (m = t.match(/(\d+(?:\.\d{1,2})?)\s?(?:pounds|quid|gbp)\b/))) intent.max = +m[1];
    }
    if (intent.around != null) { intent.min = Math.round(intent.around * 0.75); intent.max = Math.round(intent.around * 1.25); }
    if (intent.max == null && /\b(cheap|cheapest|affordable|inexpensive|budget|low cost|bargain|student)\b/.test(t)) intent.max = 25;
    if (/\b(luxury|luxurious|premium|high end|high-end|expensive|designer|niche|splurge)\b/.test(t)) intent.luxury = true;
    if (/\b(sale|deal|deals|discount|discounted|offer|offers|reduced|clearance|bargain)\b/.test(t)) intent.sale = true;
    if (/\b(in stock|available now|available|ready to ship)\b/.test(t)) intent.inStock = true;
    if (/\b(gift|gifts|present|birthday|anniversary|christmas|eid|valentine'?s?|mother'?s day|father'?s day|for my (wife|husband|mum|mom|dad|girlfriend|boyfriend|sister|brother|friend|partner)|for a friend)\b/.test(t)) intent.gift = true;
    if (/\b(surprise me|anything|random|you choose|you pick|dealer'?s choice|recommend something)\b/.test(t)) intent.surprise = true;
    if (/\b(best ?sellers?|best selling|popular|trending|top rated|most loved|favourites|favorites)\b/.test(t)) intent.bestsellers = true;

    // similarity: "like X", "dupe of X", "smells like X", "similar to X", "inspired by X", "alternative to X"
    if ((m = t.match(/(?:smells? (?:like|similar to)|similar to|dupe (?:of|for)|dupes? of|clone of|alternative to|inspired by|like)\s+([a-z0-9'&.\- ]{3,60}?)(?=\s(?:but|under|below|for|with|that|which|and|in|around|less|cheaper|from|at|max)\b|\s*$)/))) {
      const cand = m[1].trim().replace(/^(the|a|an)\s/, '');
      if (cand && !STOP.has(cand) && cand.split(' ').some((w) => !STOP.has(w))) intent.similar = cand;
    }

    // brands (from live vendor list + aliases)
    Object.keys(BRAND_ALIASES).forEach((a) => { if (hasWord(t, a)) intent.brands.push(BRAND_ALIASES[a]); });
    vendors.forEach((v) => { const nv = norm(v); if (nv.length > 2 && hasWord(t, nv)) intent.brands.push(v); });
    intent.brands = uniq(intent.brands.map((b) => { const found = vendors.find((v) => norm(v) === norm(b)); return found || b; }));

    // gender
    for (const g of ['unisex', 'women', 'men']) { if (GENDER[g].some((w) => hasWord(t, w))) { intent.gender = g; break; } }

    // category (most specific first)
    for (const c of ['bodyspray', 'gift', 'haircare', 'makeup', 'skincare', 'bath', 'fragrance']) {
      if (CATEGORIES[c].words.some((w) => hasWord(t, w))) { intent.category = c; intent.catWords = CATEGORIES[c].words.filter((w) => hasWord(t, w) && w.length > 3).slice(0, 3); break; }
    }
    if (intent.category === 'skincare' && /\b(perfume|fragrance|scent|edp|edt)\b/.test(t)) intent.category = 'fragrance';

    // notes
    Object.keys(NOTES).forEach((key) => { if (NOTES[key].some((w) => hasWord(t, w))) intent.notes.push(key); });
    // "orange blossom" shouldn't also trigger "orange"; "rose" inside "rosemary" is handled by word boundary
    if (intent.notes.includes('orange blossom')) intent.notes = intent.notes.filter((n) => n !== 'orange');
    if (intent.notes.includes('cherry blossom')) intent.notes = intent.notes.filter((n) => n !== 'cherry');

    // families / moods
    Object.keys(FAMILIES).forEach((f) => { if (FAMILIES[f].words.some((w) => hasWord(t, w))) intent.families.push(f); });
    if (intent.families.includes('musky') && intent.families.includes('fresh') && /\bclean\b/.test(t)) intent.families = intent.families.filter((f) => f !== 'fresh' || /\bfresh\b/.test(t));

    // seasons & occasions
    Object.keys(SEASONS).forEach((s) => { if (SEASONS[s].some((w) => hasWord(t, w))) intent.seasons.push(s); });
    Object.keys(OCCASIONS).forEach((o) => { if (OCCASIONS[o].words.some((w) => hasWord(t, w))) intent.occasions.push(o); });
    if (intent.occasions.includes('office') && intent.seasons.length && /\bday\b/.test(t) === false) { /* keep */ }

    intent.performance = PERFORMANCE.some((w) => t.includes(' ' + w + ' ') || t.includes(w));
    for (const c of Object.keys(CONCENTRATION)) { if (CONCENTRATION[c].some((w) => hasWord(t, w))) { intent.concentration = c; break; } }
    if (intent.concentration === 'oil' && !/\b(attar|perfume oil|oil perfume|concentrated oil)\b/.test(t)) { if (intent.category === 'haircare' || intent.category === 'skincare') intent.concentration = null; }

    // refinements (follow-ups)
    const r = intent.refine;
    if (/\b(cheaper|less expensive|lower price|more affordable|too expensive|too pricey)\b/.test(t)) r.cheaper = true;
    if (/\b(more expensive|pricier|higher end|more premium|more luxurious)\b/.test(t)) r.pricier = true;
    if (/\b(more options|show more|more results|anything else|other options|something else|different ones|more of them|next)\b/.test(t) || /^\s*more\s*$/.test(t)) r.more = true;
    if (/\b(longer lasting|last longer|stronger|more projection)\b/.test(t)) { r.stronger = true; intent.performance = true; }
    if ((m = t.match(/more like (?:the )?(first|second|third|fourth|1st|2nd|3rd|4th|top|that|this|it|number \d|#?\d)/))) r.likeIndex = m[1];
    if (/\b(start over|reset|new search|clear)\b/.test(t)) r.reset = true;
    if (/\b(instead|rather)\b/.test(t)) r.instead = true;

    // free keywords — anything meaningful we didn't classify (e.g. product names)
    const known = new Set();
    const addKnown = (arr) => arr.forEach((w) => norm(w).split(' ').forEach((x) => known.add(x)));
    Object.values(NOTES).forEach(addKnown); Object.values(FAMILIES).forEach((f) => addKnown(f.words));
    Object.values(GENDER).forEach(addKnown); Object.values(CATEGORIES).forEach((c) => addKnown(c.words));
    Object.values(SEASONS).forEach(addKnown); Object.values(OCCASIONS).forEach((o) => addKnown(o.words));
    addKnown(PERFORMANCE); Object.values(CONCENTRATION).forEach(addKnown);
    intent.brands.forEach((b) => addKnown([b]));
    intent.keywords = t.split(' ').map((w) => w.replace(/^[.'\-]+|[.'\-]+$/g, '')).filter((w) => w.length > 2 && !STOP.has(w) && !known.has(w) && !/[\d£$€]/.test(w) && !/^(cheaper|expensive|pricier|instead|rather|longer|lasting|stronger|options|results|else|different|reset|start|over|clear|between|below|above|less|than|maximum|minimum|approx|roughly|around|within|pounds|quid|gbp|sale|deal|deals|discount|offer|offers|stock|available|luxury|premium|affordable|smell|smelling|similar|dupe|clone|alternative|inspired|wear|wearing|mostly|prefer|someone|person|birthday|present|anniversary|christmas|gift|gifts|surprise|anything|random|choose|pick|best|sellers|seller|selling|popular|trending|favourites|favorites|just|show|everything|sure|lucky|wife|husband|mum|dad|girlfriend|boyfriend|sister|brother|friend|partner|mother|father|son|daughter|treat|myself|self|loved|rated|top|vibe|juicy|romantic|clean|soft|happiest|budget|limit|matter)$/.test(w));
    if (intent.similar) intent.keywords = intent.keywords.filter((w) => !intent.similar.split(' ').includes(w));

    return intent;
  }

  function hasSubject(i) {
    return i.notes.length || i.families.length || i.brands.length || i.category || i.similar || i.keywords.length || i.seasons.length || i.occasions.length;
  }

  /** Merge a follow-up message into the previous intent. */
  function mergeIntent(prev, next, lastResults) {
    if (!prev || next.refine.reset) return next;
    const followUp = !hasSubject(next) || next.refine.instead || /^\s*(and|also|what about|how about|any|ok|okay|now|but|maybe)\b/i.test(next.raw) || Object.keys(next.refine).length > 0;
    if (!followUp) return next;
    const merged = JSON.parse(JSON.stringify(prev));
    merged.raw = next.raw;
    merged.refine = next.refine;
    ['gender', 'category', 'concentration', 'size', 'similar'].forEach((k) => { if (next[k]) merged[k] = next[k]; });
    if (next.min != null || next.max != null) { merged.min = next.min; merged.max = next.max; }
    ['notes', 'families', 'brands', 'seasons', 'occasions', 'keywords'].forEach((k) => { if (next[k].length) merged[k] = next.refine.instead ? next[k] : uniq(merged[k].concat(next[k])); });
    if (next.performance) merged.performance = true;
    if (next.sale) merged.sale = true;
    if (next.inStock) merged.inStock = true;
    if (next.luxury) merged.luxury = true;
    merged.gift = prev.gift || next.gift;
    merged.surprise = next.surprise; merged.bestsellers = next.bestsellers;
    merged.asked = Object.assign({}, prev.asked || {});
    const prices = (lastResults || []).map((p) => p.price / 100).sort((a, b) => a - b);
    if (next.refine.cheaper && prices.length) { merged.max = Math.max(5, Math.floor((merged.max ? Math.min(merged.max, prices[Math.floor(prices.length / 2)]) : prices[Math.floor(prices.length / 2)]) * 0.85)); merged.min = null; }
    if (next.refine.pricier && prices.length) { merged.min = Math.ceil(prices[prices.length - 1] * 0.9); merged.max = null; }
    if (next.refine.likeIndex && lastResults && lastResults.length) {
      const map = { first: 0, '1st': 0, top: 0, that: 0, this: 0, it: 0, second: 1, '2nd': 1, third: 2, '3rd': 2, fourth: 3, '4th': 3 };
      let idx = map[next.refine.likeIndex];
      if (idx == null) { const d = String(next.refine.likeIndex).match(/\d/); idx = d ? +d[0] - 1 : 0; }
      const ref = lastResults[Math.min(idx, lastResults.length - 1)];
      if (ref) { merged.likeProduct = ref; merged.notes = uniq(productNotes(ref).all.map(noteKey).filter(Boolean)).slice(0, 5); merged.similar = null; merged.keywords = []; merged.brands = []; }
    }
    merged.page = next.refine.more ? (prev.page || 1) + 1 : 1;
    return merged;
  }

  /* ------------------------------------------------------------------ */
  /* Product understanding                                              */
  /* ------------------------------------------------------------------ */
  const TIER_RE = {
    top: '(?:top|head|opening|opens?)',
    heart: '(?:heart|middle|mid|core)',
    base: '(?:base|dry ?down|drydown|bottom)'
  };
  function grabTier(text, tier) {
    const others = Object.keys(TIER_RE).filter((k) => k !== tier).map((k) => TIER_RE[k]).join('|');
    const re = new RegExp(TIER_RE[tier] + '\\s*(?:notes?|accords?)?\\s*(?:of|are|is|include[s]?|feature[s]?|with)?\\s*[:\\-–—]?\\s*(.{3,160}?)(?=\\s*(?:' + others + ')\\s*(?:notes?|accords?)?\\s*[:\\-–—]|\\.(?:\\s|$)|;|\\||$)', 'i');
    const m = text.match(re);
    if (!m) return [];
    return m[1].split(/,|\band\b|&|\/|\+|·/i).map((x) => x.replace(/^(notes?|of|with|a|an|the)\s+/i, '').replace(/[^a-zA-ZÀ-ſ' \-]/g, '').trim()).filter((x) => x.length > 1 && x.length < 32 && x.split(' ').length <= 4).slice(0, 6);
  }
  // Structured notes section, as written on ambientbeauty.uk:
  // "🌿 Fragrance Notes / Top Notes: 🌼 Narcissus, 🌸 Jasmine / Heart Notes: … / Base Notes: …"
  const EMOJI_RE = /(?:\p{Extended_Pictographic}|\p{Regional_Indicator})(?:️|‍|\p{Extended_Pictographic}|[\u{1F3FB}-\u{1F3FF}])*/u;
  const LABEL_RE = /\b(top|head|opening|heart|middle|mid|base|dry[- ]?down)\s+notes?\s*[:\-–—]\s*/gi;
  const tierOf = (w) => (/^(top|head|opening)/i.test(w) ? 'top' : /^(heart|middle|mid)/i.test(w) ? 'heart' : 'base');
  function tierParse(text) {
    const out = { top: [], heart: [], base: [] }, emoji = {};
    const t = String(text || '');
    const hits = [];
    let m;
    LABEL_RE.lastIndex = 0;
    while ((m = LABEL_RE.exec(t))) hits.push({ tier: tierOf(m[1]), start: m.index, from: m.index + m[0].length });
    hits.forEach((h, i) => {
      let seg = t.slice(h.from, i + 1 < hits.length ? hits[i + 1].start : t.length);
      const stop = seg.search(/\.\s|\.$|;|\||\n|\b(?:fragrance|perfume|this|the scent|ideal|perfect|longevity|sillage|size|volume|concentration)\b[^,]{12,}/i);
      if (stop > 0) seg = seg.slice(0, stop);
      seg.split(/,|\s+&\s+|\s+and\s+|\/|·|\+/i).forEach((raw) => {
        const em = raw.match(EMOJI_RE);
        let name = raw.replace(new RegExp(EMOJI_RE.source, 'gu'), ' ').replace(/[^\p{L}' \-]/gu, ' ').replace(/\s+/g, ' ').trim();
        name = name.replace(/\s+(notes?|accords?|absolute|essence|extract|oil)$/i, '').replace(/^(notes? of|of|with|a|an|the)\s+/i, '').trim();
        if (name.length < 2 || name.length > 34 || name.split(' ').length > 4) return;
        if (out[h.tier].some((x) => x.toLowerCase() === name.toLowerCase())) return;
        out[h.tier].push(name);
        if (em) emoji[name.toLowerCase()] = em[0];
      });
    });
    ['top', 'heart', 'base'].forEach((k) => { out[k] = out[k].slice(0, 12); });
    return { tiers: out, emoji, found: !!(out.top.length || out.heart.length || out.base.length) };
  }
  const _noteCache = new Map();
  function productNotes(p) {
    if (_noteCache.has(p.handle)) return _noteCache.get(p.handle);
    const d = String(p.desc || '');
    const parsed = tierParse(d);
    const tiers = parsed.found ? parsed.tiers : { top: grabTier(d, 'top'), heart: grabTier(d, 'heart'), base: grabTier(d, 'base') };
    const nd = norm(d + ' ' + p.title);
    const detected = Object.keys(NOTES).filter((k) => NOTES[k].some((w) => hasWord(nd, w)));
    const all = uniq(tiers.top.concat(tiers.heart, tiers.base).map((x) => x.toLowerCase()).concat(detected));
    const res = { tiers, emoji: parsed.emoji, structured: parsed.found, detected, all, text: nd };
    _noteCache.set(p.handle, res);
    return res;
  }
  function noteKey(n) { const nn = norm(n); return Object.keys(NOTES).find((k) => NOTES[k].some((w) => nn === w || hasWord(nn, w))) || null; }
  function genderOf(p) {
    const s = norm(p.type + ' ' + (p.tags || []).join(' ') + ' ' + p.title + ' ' + p.handle);
    if (/unisex/.test(s)) return 'unisex';
    if (/\b(women|woman|femme|her|ladies|pour femme)\b|women's/.test(s)) return 'women';
    if (/\b(men|man|homme|him|pour homme)\b|men's/.test(s)) return 'men';
    return null;
  }
  function isFragrance(p) { return /fragrance|parfum|toilette|cologne|perfume|extrait|attar|edp|edt/.test(norm(p.type + ' ' + p.title + ' ' + (p.tags || []).join(' '))); }

  /* ------------------------------------------------------------------ */
  /* Scoring                                                            */
  /* ------------------------------------------------------------------ */
  function wantedNotes(intent) {
    const direct = intent.notes.slice();
    const fam = [];
    intent.families.forEach((f) => FAMILIES[f].notes.forEach((n) => fam.push(n)));
    intent.seasons.forEach((s) => (SEASON_BIAS[s] || []).forEach((f) => { if (!intent.families.length && !intent.notes.length) FAMILIES[f].notes.slice(0, 4).forEach((n) => fam.push(n)); }));
    return { direct: uniq(direct), family: uniq(fam).filter((n) => !direct.includes(n)) };
  }

  function scoreProduct(p, intent, pos) {
    const info = productNotes(p);
    const text = info.text;
    const title = norm(p.title);
    const price = p.price / 100;
    const reasons = [];
    const matched = [];
    let s = 0;

    const want = wantedNotes(intent);
    const tierText = norm(info.tiers.top.concat(info.tiers.heart, info.tiers.base).join(' '));
    want.direct.forEach((n) => {
      const syns = NOTES[n] || [n];
      if (syns.some((w) => hasWord(tierText, w))) { s += 14; matched.push(n); }
      else if (syns.some((w) => hasWord(title, w))) { s += 12; matched.push(n); }
      else if (syns.some((w) => hasWord(text, w))) { s += 8; matched.push(n); }
      else s -= 3;
    });
    let famHits = 0;
    want.family.forEach((n) => { if ((NOTES[n] || [n]).some((w) => hasWord(text, w))) { famHits++; matched.push(n); } });
    if (want.family.length) s += Math.min(famHits, 4) * 3;
    intent.families.forEach((f) => { if (FAMILIES[f].words.some((w) => hasWord(text, w))) s += 4; });
    if (want.direct.length && matched.filter((n) => want.direct.includes(n)).length === want.direct.length && want.direct.length > 1) s += 8;

    if (intent.brands.length) {
      if (intent.brands.some((b) => norm(p.vendor) === norm(b) || title.includes(norm(b)))) { s += 18; reasons.push('brand'); }
      else s -= 25;
    }
    if (intent.similar) {
      const sim = norm(intent.similar);
      const toks = sim.split(' ').filter((w) => w.length > 2 && !STOP.has(w));
      const hitAll = toks.length && toks.every((w) => text.includes(w));
      if (text.includes(sim)) { s += 22; reasons.push('similar'); }
      else if (hitAll) { s += 14; reasons.push('similar'); }
      else if (toks.some((w) => text.includes(w))) s += 4;
    }
    intent.keywords.forEach((k) => { if (title.includes(k)) s += 7; else if (text.includes(k)) s += 3; });

    const g = genderOf(p);
    if (intent.gender) {
      if (g === intent.gender) s += 7;
      else if (g === 'unisex') s += 3;
      else if (g) s -= 14;
    }

    if (intent.category) {
      const c = CATEGORIES[intent.category];
      const hay = norm(p.type + ' ' + p.title + ' ' + (p.tags || []).join(' '));
      if (c.match.some((w) => hay.includes(w))) s += 9;
      else if (intent.category === 'fragrance' && isFragrance(p)) s += 9;
      else s -= 12;
      if (intent.category !== 'bodyspray' && intent.category !== 'gift' && intent.category !== 'fragrance' && isFragrance(p)) s -= 8;
      if (intent.category === 'fragrance' && /body spray|body mist|deodorant/.test(hay)) s -= 6;
    } else if (!intent.brands.length && (intent.notes.length || intent.families.length)) {
      if (isFragrance(p)) s += 4;
    }

    intent.seasons.forEach((se) => { if (SEASONS[se].some((w) => hasWord(text, w)) || (se === 'autumn' && hasWord(text, 'fall'))) { s += 5; reasons.push(se); } });
    intent.occasions.forEach((o) => { if (OCCASIONS[o].desc.some((w) => text.includes(w))) { s += 4; reasons.push(o); } });
    if (intent.performance && PERF_DESC.some((w) => text.includes(w))) { s += 5; reasons.push('performance'); }
    if (intent.performance && /extrait|parfum|elixir|intense|absolu/.test(title)) s += 3;
    if (intent.concentration) {
      const cw = CONCENTRATION[intent.concentration];
      if (cw.some((w) => hasWord(title, w) || hasWord(norm((p.tags || []).join(' ')), w))) s += 6; else s -= 2;
    }
    if (intent.size) { if (new RegExp('\\b' + intent.size + '\\s?ml').test(title)) s += 6; }

    // price
    const inBudget = (intent.min == null || price >= intent.min - 0.001) && (intent.max == null || price <= intent.max + 0.001);
    if (intent.min != null || intent.max != null) { if (inBudget) { s += 6; reasons.push('budget'); } }
    if (intent.luxury) s += Math.min(price / 15, 6);
    const onSale = p.compare > p.price;
    if (onSale) s += intent.sale ? 8 : 1.5;
    if (intent.sale && !onSale) s -= 6;

    // availability & position (relevance / best-selling order from Shopify)
    s += p.available ? 4 : -10;
    s += Math.max(0, 4 - (pos || 0) * 0.15);
    if (intent.likeProduct && p.handle === intent.likeProduct.handle) s -= 100;

    return { p, s, matched: uniq(matched), inBudget, reasons, gender: g, onSale };
  }

  function rank(products, intent) {
    const seen = new Set();
    const list = [];
    products.forEach((p, i) => { if (!p || seen.has(p.handle)) return; seen.add(p.handle); list.push(scoreProduct(p, intent, p._pos != null ? p._pos : i)); });
    const hasBudget = intent.min != null || intent.max != null;
    let pool = list;
    if (intent.inStock) pool = pool.filter((x) => x.p.available);
    let budgetMiss = false;
    if (hasBudget) {
      const inB = pool.filter((x) => x.inBudget);
      if (inB.length) pool = inB; else budgetMiss = true;
    }
    let brandMiss = false;
    if (intent.brands.length) { const b = pool.filter((x) => x.reasons.includes('brand')); if (b.length) pool = b; else brandMiss = true; }
    // relevance gate: if specific notes/moods were asked for, keep products that actually contain them
    const want = wantedNotes(intent);
    if (want.direct.length) {
      const rel = pool.filter((x) => x.matched.some((n) => want.direct.includes(n)));
      if (rel.length) pool = rel;
      else if (hasBudget && !budgetMiss) {
        const relAll = list.filter((x) => x.matched.some((n) => want.direct.includes(n)) && (!intent.inStock || x.p.available));
        if (relAll.length) { pool = relAll; budgetMiss = true; }
      }
    } else if (want.family.length) {
      const rel = pool.filter((x) => x.matched.length > 0);
      if (rel.length >= 2) pool = rel;
    }
    if (intent.gender) { const g = pool.filter((x) => !x.gender || x.gender === intent.gender || x.gender === 'unisex'); if (g.length >= 2) pool = g; }
    pool.sort((a, b) => b.s - a.s || (a.p.price - b.p.price));
    // drop weak matches when the request was specific
    const specific = intent.keywords.length || intent.similar || intent.brands.length || want.direct.length || intent.category;
    if (specific && pool.length > 3 && pool[0].s > 12) { const cut = pool[0].s * 0.5; const strong = pool.filter((x) => x.s >= cut); if (strong.length >= 1) pool = strong; }
    pool = pool.filter((x, i) => i < 3 || x.s > -5);
    if (budgetMiss) {
      const target = intent.max != null ? intent.max : intent.min;
      pool.sort((a, b) => Math.abs(a.p.price / 100 - target) - Math.abs(b.p.price / 100 - target) || b.s - a.s);
    }
    return { ranked: pool, budgetMiss, brandMiss };
  }

  function buildRequests(intent, cfg) {
    const reqs = [];
    const root = cfg.root || '';
    const priceParams = () => {
      const q = [];
      if (intent.min != null) q.push('filter.v.price.gte=' + intent.min);
      if (intent.max != null) q.push('filter.v.price.lte=' + intent.max);
      if (intent.inStock) q.push('filter.v.availability=1');
      return q.join('&');
    };
    const page = intent.page || 1;
    const terms = [];
    intent.brands.forEach((b) => terms.push(b));
    if (intent.similar) terms.push(intent.similar);
    const want = wantedNotes(intent);
    want.direct.forEach((n) => terms.push((NOTES[n] || [n])[0]));
    intent.keywords.slice(0, 4).forEach((k) => terms.push(k));
    if (intent.category && !['fragrance', 'gift'].includes(intent.category) && intent.catWords) intent.catWords.filter((w) => !['skin', 'face', 'hair', 'body', 'bath', 'makeup', 'make up', 'skincare', 'haircare'].includes(w)).forEach((w) => terms.push(w));
    const famTerms = want.family.slice(0, 6).map((n) => (NOTES[n] || [n])[0]);

    const search = (q, sort) => root + '/search?q=' + encodeURIComponent(q) + '&type=product&options%5Bprefix%5D=last&options%5Bunavailable_products%5D=last' + (sort ? '&sort_by=' + sort : '') + '&page=' + page + '&section_id=ai-results' + (priceParams() ? '&' + priceParams() : '');
    const coll = (handle, sort) => root + '/collections/' + handle + '?sort_by=' + (sort || 'best-selling') + '&page=' + page + '&section_id=ai-results' + (priceParams() ? '&' + priceParams() : '');

    if (terms.length) {
      // precise (AND) and broad (OR) passes
      reqs.push(search(terms.join(' ')));
      if (terms.length > 1) reqs.push(search(terms.map((x) => x.includes(' ') ? '"' + x + '"' : x).join(' OR ')));
      if (intent.notes.length && (intent.gender || intent.category)) {
        const extra = intent.gender === 'women' ? 'women' : intent.gender === 'men' ? 'men' : intent.gender === 'unisex' ? 'unisex' : '';
        if (extra) reqs.push(search(terms.slice(0, 2).join(' ') + ' ' + extra));
      }
    } else if (famTerms.length) {
      reqs.push(search(famTerms.join(' OR ')));
    }
    if (famTerms.length && terms.length) reqs.push(search(famTerms.slice(0, 5).join(' OR ')));

    // collection pass for gender/category/budget browsing
    const C = cfg.collections || {};
    let handle = null;
    if (intent.category && intent.category !== 'fragrance') handle = C[CATEGORIES[intent.category].coll];
    else if (intent.gender) handle = C[intent.gender];
    else if (intent.category === 'fragrance') handle = C.fragrance;
    if (handle) reqs.push(coll(handle, intent.luxury ? 'price-descending' : 'best-selling'));
    if (!reqs.length || (!terms.length && !famTerms.length && !handle)) reqs.push(coll(C.all || 'all', intent.luxury ? 'price-descending' : (intent.max != null ? 'best-selling' : 'best-selling')));
    if (intent.sale && !terms.length) reqs.push(coll(handle || C.all || 'all', 'best-selling'));
    return uniq(reqs).slice(0, 5);
  }

  async function fetchJSON(url, signal) {
    const res = await fetch(url, { signal, credentials: 'same-origin' });
    if (!res.ok) return [];
    const html = await res.text();
    const m = html.match(/<script[^>]*data-ai-results[^>]*>([\s\S]*?)<\/script>/);
    if (!m) return [];
    try { const data = JSON.parse(m[1]); return (data.products || []).map((p, i) => Object.assign(p, { _pos: i })); } catch (e) { return []; }
  }

  /* ------------------------------------------------------------------ */
  /* Answer writing                                                     */
  /* ------------------------------------------------------------------ */
  function money(cents, fmt) {
    const v = (cents / 100);
    const f = fmt || '£{{amount}}';
    return f.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
      if (k === 'amount_no_decimals') return Math.round(v).toLocaleString('en-GB');
      if (k === 'amount_with_comma_separator') return v.toFixed(2).replace('.', ',');
      return v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    });
  }
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const shortTitle = (p) => {
    let t = p.title.replace(new RegExp('^' + escapeRe(p.vendor) + '\\s*', 'i'), '');
    t = t.replace(/\b(eau de parfum|eau de toilette|extrait de parfum|edp|edt|parfum)\b.*$/i, '').replace(/\s+\d+\s?ml.*$/i, '').trim();
    return cap((t || p.title).toLowerCase());
  };
  const brandName = (p) => cap(String(p.vendor || '').toLowerCase());

  function d_like(intent) {
    if (intent.likeProduct) return 'similar to ' + shortTitle(intent.likeProduct);
    if (intent.similar) return 'like “' + cap(intent.similar) + '”';
    return '';
  }
  function describeIntent(intent) {
    const bits = [];
    const notes = intent.notes.map((n) => n);
    const fams = intent.families.map((f) => FAMILIES[f].label);
    const cat = intent.category ? ({ fragrance: 'fragrances', bodyspray: 'body sprays', gift: 'gift sets', skincare: 'skincare picks', haircare: 'haircare picks', makeup: 'makeup picks', bath: 'bath & body picks' })[intent.category] : (intent.brands.length && !notes.length && !fams.length ? 'picks' : 'fragrances');
    let core = '';
    if (notes.length) core = listify(notes.slice(0, 3)) + (fams.length ? ' (' + fams.join(', ') + ')' : '');
    else if (fams.length) core = listify(fams);
    if (intent.brands.length && !intent._brandMiss) core = (intent.brands.map(cap).join(' & ') + ' ' + core).trim();
    let phrase = (intent.performance ? 'long-lasting ' : '') + (core ? core + ' ' : '') + cat;
    if (d_like(intent)) phrase += ' ' + d_like(intent);
    if (intent.gender) phrase += intent.gender === 'unisex' ? ' (unisex)' : (intent.gender === 'women' ? ' for her' : ' for him');
    if (intent.min != null && intent.max != null) phrase += ' between £' + intent.min + ' and £' + intent.max;
    else if (intent.max != null) phrase += ' under £' + intent.max;
    else if (intent.min != null) phrase += ' over £' + intent.min;
    if (intent.seasons.length) phrase += ' for ' + listify(intent.seasons);
    if (intent.occasions.length) phrase += (intent.seasons.length ? ' and ' : ' for ') + listify(intent.occasions.map((o) => ({ office: 'everyday wear', evening: 'evenings', special: 'special occasions', gym: 'the gym' })[o]));
    if (intent.sale) phrase += ', on offer';
    return { phrase: phrase, bits };
  }

  function chipsFor(intent, top) {
    const c = [];
    if (intent.max != null) c.push('Cheaper options'); else c.push('Under £30');
    if (intent.gender !== 'women') c.push('For her instead');
    if (intent.gender !== 'men') c.push('For him instead');
    if (!intent.performance && (!intent.category || intent.category === 'fragrance')) c.push('Longer lasting');
    if (top) c.push('More like the first one');
    if (!intent.sale) c.push('Only deals');
    c.push('Show more');
    return c.slice(0, 6);
  }

  function understoodTags(intent) {
    const t = [];
    intent.notes.forEach((n) => t.push('Note: ' + n));
    intent.families.forEach((f) => t.push('Mood: ' + FAMILIES[f].label));
    if (intent.gender) t.push({ women: 'For her', men: 'For him', unisex: 'Unisex' }[intent.gender]);
    if (intent.min != null && intent.max != null) t.push('£' + intent.min + '–£' + intent.max);
    else if (intent.max != null) t.push('Under £' + intent.max);
    else if (intent.min != null) t.push('Over £' + intent.min);
    intent.brands.forEach((b) => t.push('Brand: ' + cap(b.toLowerCase())));
    if (intent.category) t.push(cap(intent.category === 'bodyspray' ? 'body spray' : intent.category));
    intent.seasons.forEach((s) => t.push(cap(s)));
    intent.occasions.forEach((o) => t.push(cap(o === 'office' ? 'everyday' : o)));
    if (intent.performance) t.push('Long-lasting');
    if (intent.concentration) t.push(cap(intent.concentration));
    if (intent.similar) t.push('Like: ' + cap(intent.similar));
    if (intent.likeProduct) t.push('Like: ' + shortTitle(intent.likeProduct));
    if (intent.sale) t.push('On offer');
    return t.slice(0, 8);
  }

  /* ------------------------------------------------------------------ */
  /* Full catalogue (reads every product + description once, cached)    */
  /* ------------------------------------------------------------------ */
  const Catalog = {
    items: null,
    loading: null,
    progress: 0,
    async load(cfg) {
      if (this.items) return this.items;
      if (this.loading) return this.loading;
      const KEY = 'ambient-ai-catalog-v2';
      this.loading = (async () => {
        const cached = await idb.get(KEY);
        if (cached && cached.t > Date.now() - 6 * 3600e3 && cached.items && cached.items.length) { this.items = cached.items; this.progress = 1; return this.items; }
        const url = (n) => (cfg.root || '') + '/collections/' + ((cfg.collections && cfg.collections.all) || 'all') + '?sort_by=best-selling&section_id=ai-index&page=' + n;
        const first = await fetchFeed(url(1));
        let items = first.products || [];
        const pages = Math.min(first.pages || 1, 40);
        const queue = []; for (let n = 2; n <= pages; n++) queue.push(n);
        const worker = async () => { while (queue.length) { const n = queue.shift(); const d = await fetchFeed(url(n)).catch(() => ({ products: [] })); items = items.concat((d.products || []).map((p, i) => Object.assign(p, { _pos: (n - 1) * 250 + i }))); this.progress = items.length / (first.total || items.length); } };
        (first.products || []).forEach((p, i) => { p._pos = i; });
        await Promise.all([worker(), worker(), worker()]);
        items.sort((a, b) => a._pos - b._pos);
        this.items = items; this.progress = 1;
        idb.set(KEY, { t: Date.now(), items }).catch(() => {});
        return items;
      })().catch(() => { this.loading = null; return null; });
      return this.loading;
    }
  };
  async function fetchFeed(url) {
    const res = await fetch(url, { credentials: 'same-origin' });
    const html = await res.text();
    const m = html.match(/<script[^>]*data-ai-index[^>]*>([\s\S]*?)<\/script>/);
    return m ? JSON.parse(m[1]) : { products: [] };
  }
  const idb = {
    db: null,
    open() {
      if (this.db) return Promise.resolve(this.db);
      return new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') return reject(new Error('no idb'));
        const r = indexedDB.open('ambient-ai', 1);
        r.onupgradeneeded = () => r.result.createObjectStore('kv');
        r.onsuccess = () => { this.db = r.result; resolve(this.db); };
        r.onerror = () => reject(r.error);
      });
    },
    async get(k) { try { const db = await this.open(); return await new Promise((res) => { const q = db.transaction('kv').objectStore('kv').get(k); q.onsuccess = () => res(q.result); q.onerror = () => res(null); }); } catch (e) { return null; } },
    async set(k, v) { const db = await this.open(); return new Promise((res) => { const tx = db.transaction('kv', 'readwrite'); tx.objectStore('kv').put(v, k); tx.oncomplete = res; tx.onerror = res; }); }
  };

  /* ------------------------------------------------------------------ */
  /* Retrieval: whole catalogue when ready, live search otherwise       */
  /* ------------------------------------------------------------------ */
  async function fetchResults(url, signal) {
    const res = await fetch(url, { signal, credentials: 'same-origin' });
    if (!res.ok) return [];
    const html = await res.text();
    const m = html.match(/<script[^>]*data-ai-results[^>]*>([\s\S]*?)<\/script>/);
    if (!m) return [];
    try { return (JSON.parse(m[1]).products || []).map((p, i) => Object.assign(p, { _pos: i })); } catch (e) { return []; }
  }
  async function retrieve(intent, cfg, signal) {
    let products = null;
    if (Catalog.items) products = Catalog.items;
    else if (Catalog.loading) products = await Promise.race([Catalog.loading, new Promise((r) => setTimeout(() => r(null), 1400))]);
    if (!products) {
      const urls = buildRequests(intent, cfg);
      const batches = await Promise.all(urls.map((u) => fetchResults(u, signal).catch(() => [])));
      products = [].concat.apply([], batches);
    }
    return products.map((p) => { if (!p.url) p.url = (cfg.root || '') + '/products/' + p.handle; return p; });
  }

  /* ------------------------------------------------------------------ */
  /* The advisor: dialogue manager                                      */
  /* ------------------------------------------------------------------ */
  const NOTE_FEEL = {
    vanilla: 'Vanilla is such a comforting choice — warm, creamy and quietly addictive.',
    oud: 'Oud is the soul of Arabian perfumery — rich, resinous and unforgettable.',
    rose: 'Rose never goes out of style — romantic, elegant and endlessly versatile.',
    amber: 'Amber gives that warm, glowing, wrapped-in-cashmere feeling.',
    musk: 'Musk is the ultimate “your skin but better” note — soft, clean and intimate.',
    saffron: 'Saffron adds a gorgeous golden, leathery spice — very luxurious.',
    sandalwood: 'Sandalwood is creamy, smooth and calming — a beautiful base.',
    jasmine: 'Jasmine is lush, heady and sensual — a true floral icon.',
    caramel: 'Caramel brings a delicious, golden sweetness — very moreish.',
    tonka: 'Tonka is warm, almondy and a little powdery — pure cosiness.',
    coffee: 'Coffee notes are rich, roasted and seriously addictive.',
    leather: 'Leather is bold, smooth and confident — a statement-maker.',
    tobacco: 'Tobacco is warm, honeyed and sophisticated.',
    coconut: 'Coconut feels sunny, creamy and instantly holiday-mode.',
    lavender: 'Lavender is fresh, herbal and calming — lovely in sweet blends too.',
    bergamot: 'Bergamot is sparkling and bright — a fresh, uplifting opener.',
    citrus: 'Citrus is bright, zesty and full of energy.',
    marine: 'Marine notes feel breezy, salty and effortlessly fresh.',
    incense: 'Incense is mystical, smoky and deeply elegant.',
    patchouli: 'Patchouli is earthy, dark and grounding.',
    cherry: 'Cherry is juicy, playful and a little bit flirty.',
    pineapple: 'Pineapple adds a juicy, fruity sparkle — famously crowd-pleasing.',
    peach: 'Peach is soft, velvety and sunny.',
    honey: 'Honey is golden, rich and sensual.',
    pistachio: 'Pistachio is nutty, creamy and so on-trend.',
    iris: 'Iris is powdery, refined and quietly luxurious.',
    tuberose: 'Tuberose is creamy, opulent and unashamedly glamorous.'
  };
  const FAMILY_FEEL = {
    sweet: 'Sweet scents are such a joy — cosy, addictive and compliment magnets.',
    floral: 'Florals are a beautiful choice — romantic, elegant and timeless.',
    woody: 'Woody scents feel grounded, refined and effortlessly classy.',
    fresh: 'Fresh scents are perfect for feeling clean, confident and energised.',
    amber: 'Warm, spicy scents feel opulent and wrap you up beautifully.',
    fruity: 'Fruity scents are playful, juicy and full of personality.',
    musky: 'Soft musky scents are intimate and elegant — like your skin, but better.',
    dark: 'Dark, smoky scents are bold, mysterious and leave a real impression.'
  };
  const QUICK_VIBES = ['Sweet & vanilla', 'Fresh & clean', 'Floral & romantic', 'Oud & woody', 'Warm & spicy', 'Fruity & juicy', 'Soft & musky', 'Just show best sellers'];
  const QUICK_WHO = ['For her', 'For him', 'Unisex', "Not sure — show me everything"];
  const QUICK_BUDGET = ['Under £20', 'Under £30', 'Under £50', 'Under £80', 'No budget'];

  const timeGreeting = () => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'; };

  function metaIntent(text, intent) {
    const t = ' ' + norm(text) + ' ';
    const product = intent.notes.length || intent.families.length || intent.brands.length || (intent.category && intent.category !== 'fragrance') || intent.similar;
    const shopping = product || intent.gender || intent.min != null || intent.max != null;
    const short = t.trim().split(/\s+/).length <= 6;
    if (/^\s*(hi+|hello|hey+|hiya|yo|salam|salaam|assalam\w*|good (morning|afternoon|evening))\b/.test(t) && !shopping) return 'greeting';
    if (/\b(thanks|thank you|thx|cheers|ty|appreciate it|perfect thanks|great thanks)\b/.test(t) && short) return 'thanks';
    if (/\b(who are you|what are you|are you (a )?(bot|robot|human|real|ai))\b/.test(t)) return 'identity';
    if (/\b(track|tracking|where is my order|order status|my order)\b/.test(t)) return 'tracking';
    if (/\b(return|returns|refund|exchange)\b/.test(t) && !shopping) return 'returns';
    if (/\b(deliver|delivery|shipping|ship|dispatch|arrive|postage|next day)\b/.test(t) && !product) return 'delivery';
    if (/\b(authentic|genuine|real|fake|original|legit)\b/.test(t) && !product) return 'authentic';
    if (/\b(klarna|clearpay|paypal|apple pay|google pay|pay later|instal+ments?|payment)\b/.test(t)) return 'payment';
    if (/\b(discount|code|coupon|promo|voucher)\b/.test(t) && !product) return 'discount';
    if (/\b(speak to|talk to|human|agent|customer service|contact|phone|email you)\b/.test(t) && !shopping) return 'contact';
    return null;
  }

  function infoAnswer(kind, cfg) {
    const free = cfg.freeShipping ? `free UK delivery on orders over £${cfg.freeShipping}` : 'fast UK delivery';
    switch (kind) {
      case 'greeting': return { paragraphs: [`${timeGreeting()}! 👋 I’m ${esc(cfg.name || 'your scent advisor')} — think of me as your personal fragrance expert.`, 'Tell me what you’re in the mood for and I’ll find it with you. Is it a treat for yourself, or a gift for someone special?'], chips: ['Something for me', 'A gift', 'Sweet & vanilla', 'Fresh & clean', 'Oud & woody', 'Surprise me'] };
      case 'thanks': return { paragraphs: [pick(['My pleasure! 💜', 'Anytime — that’s what I’m here for!', 'You’re so welcome!'], Date.now()), 'Would you like me to find a matching body spray, or look for something else?'], chips: ['Find a matching body spray', 'Something else', 'Show me deals'] };
      case 'identity': return { paragraphs: [`I’m ${esc(cfg.name || 'Ambient AI')}, the store’s scent advisor. I read every product and its notes so I can match fragrances to your taste, budget and moment.`, 'Ask me anything — “a sweet vanilla perfume under £30”, “something like Baccarat Rouge”, or “a gift for my dad”.'], chips: ['A gift', 'Something for me', 'Best sellers'] };
      case 'delivery': return { paragraphs: [`We offer ${free}. ${esc(cfg.deliveryText || 'UK delivery usually takes 1–3 working days')}.`, cfg.cutoff ? `Order before ${cfg.cutoff}:00 on a weekday and it’s dispatched the same day.` : '', 'Shall I help you find something to add to your bag?'].filter(Boolean), chips: ['Best sellers', 'Under £30', 'Gift sets'] };
      case 'returns': return { paragraphs: [esc(cfg.returnsText || 'If something isn’t right, our team will happily help with a return — see our refund policy for the details.'), 'Anything I can help you choose with confidence?'], chips: ['Help me choose', 'Best sellers'], link: cfg.refundUrl ? { href: cfg.refundUrl, label: 'Refund policy' } : null };
      case 'authentic': return { paragraphs: ['Every product we sell is 100% authentic, sourced from authorised distributors — guaranteed.', 'Want me to show you our most-loved originals?'], chips: ['Best sellers', 'Lattafa favourites', 'Designer picks'] };
      case 'payment': return { paragraphs: ['You can pay securely at checkout with all major cards, Apple Pay, Google Pay, PayPal and Shop Pay — any pay-later options available to you will be shown there too.', 'Shall we find something lovely to try?'], chips: ['Best sellers', 'Under £30', 'Gift sets'] };
      case 'discount': return { paragraphs: [`Subscribe to our newsletter for 5% off your first order, and don’t miss the sale — up to 60% off selected scents right now.`, 'Want me to show you today’s best deals?'], chips: ['Show me deals', 'Deals for her', 'Deals for him'] };
      case 'tracking': return { paragraphs: ['You can track your order any time from the link below — you’ll just need your order number and email.', 'Anything else I can help with while you’re here?'], chips: ['Best sellers', 'New arrivals'], link: cfg.trackUrl ? { href: cfg.trackUrl, label: 'Track my order' } : null };
      case 'contact': return { paragraphs: ['Our friendly team is happy to help (Mon–Fri, 8am–6pm). You can reach them from our contact page.', 'In the meantime, I’m right here if you need product advice!'], chips: ['Help me choose', 'Best sellers'], link: cfg.contactUrl ? { href: cfg.contactUrl, label: 'Contact us' } : null };
    }
    return null;
  }

  function needsQuestion(intent) {
    if (intent.surprise || intent.bestsellers || intent.sale || intent.refine.more || intent.likeProduct) return null;
    const asked = intent.asked || {};
    const subject = intent.notes.length || intent.families.length || intent.brands.length || intent.similar || intent.keywords.length || (intent.category && intent.category !== 'fragrance' && intent.category !== 'gift');
    if (subject) {
      if (intent.gift && !intent.gender && !asked.who) return 'who';
      return null;
    }
    if (!intent.gender && !asked.who && (intent.gift || (!intent.category && intent.min == null && intent.max == null && !intent.seasons.length && !intent.occasions.length))) return 'who';
    if (!asked.vibe && !intent.seasons.length && !intent.occasions.length) return 'vibe';
    if (intent.gift && intent.min == null && intent.max == null && !asked.budget) return 'budget';
    return null;
  }

  function questionFor(slot, intent, cfg) {
    const seed = hash(intent.raw || '') + (intent.turn || 0);
    if (slot === 'who') return {
      paragraphs: [intent.gift ? pick(['What a lovely idea — I’d love to help you pick the perfect gift. 🎁', 'Ooh, gifting! Let’s find something they’ll treasure. 🎁'], seed) : pick(['Happy to help you find something special!', 'Let’s find your perfect match together.'], seed), intent.gift ? 'Who’s the lucky person?' : 'Is it for you or someone else — and who will be wearing it?'],
      chips: intent.gift ? ['For her', 'For him', 'Unisex', 'Not sure — show me everything'] : QUICK_WHO
    };
    if (slot === 'vibe') return {
      paragraphs: [pick(['Lovely.', 'Great.', 'Perfect.'], seed) + ' ' + (intent.gift ? 'What kind of scents do they usually love?' : 'What kind of scent makes you happiest?'), 'Pick a vibe below — or just tell me a note you love, like vanilla, oud or rose.'],
      chips: QUICK_VIBES
    };
    if (slot === 'budget') return {
      paragraphs: ['Last one, I promise 😊 — do you have a budget in mind?'],
      chips: QUICK_BUDGET
    };
    return null;
  }

  function extractHighlights(p) {
    const d = String(p.desc || '');
    const m1 = d.match(/(?:perfect|ideal|great|suited|made|designed)\s+for\s+([^.;:!]{4,70})/i);
    return { ideal: m1 ? m1[1].trim().replace(/\s+/g, ' ').replace(/\s+(and|or|,)$/, '') : '', lasting: /long[- ]lasting|all[- ]day|longevity|beast mode|projection|sillage/i.test(d) };
  }

  function findBundle(top, pool, cfg) {
    if (!top || !pool) return null;
    const isAddon = (p) => /body spray|body mist|deodorant|deo\b|hair mist|hair & body mist|shower gel|body lotion|\bmist\b|travel|mini/i.test(p.title + ' ' + p.type);
    const tg = genderOf(top.p);
    const genderOk = (p) => { const g = genderOf(p); return !tg || !g || g === tg || g === 'unisex' || tg === 'unisex'; };
    const ok = (p) => p.handle !== top.p.handle && p.available && p.single && p.v && p.price > 0 && genderOk(p);
    const vendor = norm(top.p.vendor);
    let pickP = isAddon(top.p) ? null : pool.find((p) => ok(p) && norm(p.vendor) === vendor && isAddon(p));
    let reason = 'brand';
    if (!pickP) {
      const topNotes = productNotes(top.p).detected;
      pickP = pool.filter((p) => ok(p) && isAddon(p) && p.price <= 2500).find((p) => productNotes(p).detected.some((n) => topNotes.includes(n)));
      reason = 'layer';
    }
    if (!pickP) return null;
    return { a: top.p, b: pickP, reason, total: top.p.price + pickP.price };
  }

  function freeShippingNudge(total, cfg, pool, exclude) {
    const free = (cfg.freeShipping || 0) * 100;
    if (!free || total >= free || total < free * 0.4) return null;
    const gap = free - total;
    const cands = (pool || []).filter((p) => p.available && p.single && p.v && p.price >= gap && p.price <= gap + 1500 && !exclude.includes(p.handle));
    const addon = (p) => /body spray|body mist|deodorant|mist|travel|mini|shower gel|lotion/i.test(p.title + ' ' + p.type);
    const filler = cands.filter(addon).sort((a, b) => a.price - b.price)[0] || cands.sort((a, b) => a.price - b.price)[0];
    return { gap, filler };
  }

  function writeAdvice(intent, result, cfg, pool) {
    const items = result.ranked;
    const fmt = cfg.moneyFormat;
    const seed = hash((intent.raw || '') + (intent.turn || 0));
    const P = [];
    if (!items.length) {
      return {
        paragraphs: ['Hmm, I couldn’t find an exact match for that just yet — but don’t worry, we’ll get there together.', 'Could you tell me a little more? A note you love, the mood you’re after, or a price range all help.'],
        chips: ['Sweet & vanilla', 'Fresh & clean', 'Oud & woody', 'Best sellers', 'Under £30']
      };
    }
    const top = items[0];
    // empathetic opener
    let opener = '';
    const firstNote = intent.notes.find((n) => NOTE_FEEL[n]);
    if (intent.likeProduct) opener = `If you love ${esc(shortTitle(intent.likeProduct))}, you’ll feel right at home with these — they share its key notes.`;
    else if (intent.similar) opener = `Great reference! I’ve searched every description for fragrances inspired by or close to ${esc(cap(intent.similar))}.`;
    else if (firstNote) opener = NOTE_FEEL[firstNote];
    else if (intent.families.length && FAMILY_FEEL[intent.families[0]]) opener = FAMILY_FEEL[intent.families[0]];
    else if (intent.gift) opener = 'What a thoughtful gift — I’ve picked options that always go down beautifully.';
    else if (intent.surprise) opener = 'Ooh, I love a surprise! Here’s something I think you’ll adore.';
    else if (intent.brands.length && !result.brandMiss) opener = `${esc(intent.brands.map(cap).join(' & '))} is a brilliant choice — here are my favourites right now.`;
    else if (intent.sale) opener = 'Love a bargain! These are the best-value picks right now.';
    else opener = pick(['Lovely — here’s what I’d recommend.', 'Great choice. Here’s what I’d pick for you.'], seed);
    if (result.brandMiss) opener = `I couldn’t find ${esc(intent.brands.map(cap).join(' or '))} in stock right now, so here are the closest matches I’d recommend instead.`;
    const d = describeIntent(intent);
    const scope = [];
    if (intent.gender) scope.push(intent.gender === 'women' ? 'for her' : intent.gender === 'men' ? 'for him' : 'unisex');
    if (intent.max != null) scope.push('under £' + intent.max);
    if (intent.min != null && intent.max == null) scope.push('over £' + intent.min);
    P.push(opener + (scope.length && !result.budgetMiss ? ` I’ve kept everything ${scope.join(', ')}.` : ''));
    if (result.budgetMiss) P.push(`Nothing quite fit ${intent.max != null ? 'under £' + intent.max : 'that price'}, so I’ve chosen the closest — from ${money(Math.min.apply(null, items.slice(0, 6).map((x) => x.p.price)), fmt)}.`);

    // top pick story
    const n = productNotes(top.p);
    const hl = (arr) => arr.map((x) => { const k = noteKey(x); return k && top.matched.includes(k) ? '<mark>' + esc(x.toLowerCase()) + '</mark>' : esc(x.toLowerCase()); });
    let story = '';
    if (n.tiers.top.length || n.tiers.heart.length || n.tiers.base.length) {
      const seg = [];
      if (n.tiers.top.length) seg.push('opens with ' + listify(hl(n.tiers.top.slice(0, 3))));
      if (n.tiers.heart.length) seg.push('reveals ' + listify(hl(n.tiers.heart.slice(0, 3))) + ' at the heart');
      if (n.tiers.base.length) seg.push('settles into ' + listify(hl(n.tiers.base.slice(0, 3))));
      story = ' It ' + listify(seg) + '.';
    } else if (top.matched.length) story = ' You’ll get lovely ' + listify(top.matched.slice(0, 3).map((m) => '<mark>' + esc(m) + '</mark>')) + ' notes.';
    const hi = extractHighlights(top.p);
    const hiBit = (hi.ideal ? ` It’s ideal for ${esc(hi.ideal.toLowerCase())}.` : '') + (hi.lasting ? ' Expect excellent longevity, too.' : '');
    const priceBit = top.onSale ? `${money(top.p.price, fmt)} — that’s ${Math.round((1 - top.p.price / top.p.compare) * 100)}% off` : money(top.p.price, fmt);
    P.push(`${pick(['My top pick for you is', 'If I had to choose one, it’s', 'The one I’d reach for first is'], seed)} <strong>${esc(brandName(top.p))} ${esc(shortTitle(top.p))}</strong>.${story}${hiBit} ${priceBit}${top.p.available ? '.' : ' — it’s sold out right now, so save it to your wishlist and I’ll keep looking.'}`);

    // alternatives with character
    const rest = items.slice(1, 6);
    const alt = [];
    const cheaper = rest.filter((x) => x.p.price < top.p.price * 0.85).sort((a, b) => b.s - a.s)[0];
    if (cheaper) alt.push(`for great value, <strong>${esc(brandName(cheaper.p))} ${esc(shortTitle(cheaper.p))}</strong> at ${money(cheaper.p.price, fmt)}`);
    const different = rest.find((x) => x !== cheaper && x.p.vendor !== top.p.vendor);
    if (different) {
      const dn = different.matched.length ? different.matched.slice(0, 2).join(' & ') : productNotes(different.p).all.slice(0, 2).join(' & ');
      alt.push(`for something a little different, <strong>${esc(brandName(different.p))} ${esc(shortTitle(different.p))}</strong>${dn ? ' (' + esc(dn) + ')' : ''}`);
    }
    if (alt.length) { const l = listify(alt, 'or'); P.push(l.charAt(0).toUpperCase() + l.slice(1) + '.'); }
    return { paragraphs: P, chips: chipsFor(intent, top), top };
  }

  /**
   * One conversational turn. Pure apart from `deps.retrieve`, so it can be tested.
   * state: { intent, last, pending, turn }
   */
  async function respond(text, state, cfg, deps) {
    state.turn = (state.turn || 0) + 1;
    const parsed = parseIntent(text, { vendors: cfg.vendors || [] });
    const meta = metaIntent(text, parsed);
    if (meta && !state.pending) {
      const info = infoAnswer(meta, cfg);
      if (meta === 'thanks' && /matching body spray/i.test(text)) { /* handled as shopping below */ } else if (info) return Object.assign({ kind: 'info' }, info);
    }

    // continue a guided flow
    let intent;
    if (state.pending) {
      const base = state.pending.base;
      const t = norm(text);
      intent = mergeIntent(base, Object.assign(parsed, { refine: Object.assign({}, parsed.refine, { instead: false }) }), state.last || []);
      intent.asked = Object.assign({}, base.asked, { [state.pending.slot]: true });
      intent.gift = base.gift || parsed.gift;
      if (/not sure|everything|anyone/.test(t) && state.pending.slot === 'who') intent.gender = null;
      if (/no budget|any budget|doesn.t matter|no limit/.test(t)) { intent.min = null; intent.max = null; intent.asked.budget = true; }
      if (/just show|best ?sellers|skip/.test(t)) { intent.bestsellers = true; intent.asked.vibe = true; }
      state.pending = null;
    } else if (parsed.surprise || parsed.bestsellers && !hasSubject(parsed)) {
      intent = parsed; intent.asked = {};
    } else {
      intent = mergeIntent(state.intent, parsed, state.last || []);
      intent.asked = (state.intent && intent !== parsed) ? Object.assign({}, state.intent.asked || {}) : {};
    }
    intent.turn = state.turn;
    if (/find a matching body spray/i.test(text) && state.last && state.last[0]) {
      intent = parseIntent(state.last[0].vendor + ' body spray', { vendors: cfg.vendors || [] });
      intent.turn = state.turn; intent.asked = {};
    }

    const slot = needsQuestion(intent);
    if (slot) {
      state.pending = { slot, base: intent };
      const q = questionFor(slot, intent, cfg);
      return Object.assign({ kind: 'ask', intent }, q);
    }

    const products = await deps.retrieve(intent);
    let result;
    if (intent.surprise) {
      const pool = products.filter((p) => p.available).slice(0, 40);
      const r = rank(pool, intent).ranked;
      const k = Math.floor(Math.random() * Math.min(8, r.length));
      result = { ranked: r.slice(k).concat(r.slice(0, k)), budgetMiss: false, brandMiss: false };
    } else result = rank(products, intent);
    const shown = result.ranked.slice(0, cfg.limit || 8);
    const advice = writeAdvice(intent, result, cfg, products);
    const bundle = shown.length ? findBundle(shown[0], products, cfg) : null;
    const nudge = shown.length ? freeShippingNudge(bundle ? bundle.total : shown[0].p.price, cfg, products, [shown[0].p.handle, bundle ? bundle.b.handle : '']) : null;
    const closing = shown.length ? pick([
      'How do these feel? I can make it sweeter, fresher, longer-lasting or cheaper — just say the word.',
      'Want me to fine-tune this? Tell me what you’d change and I’ll adjust.',
      'Shall I narrow it down further, or find something similar but different?'
    ], hash(text) + state.turn) : '';
    state.intent = intent;
    state.last = shown.map((x) => x.p);
    return { kind: 'answer', intent, paragraphs: advice.paragraphs, products: shown, chips: advice.chips, bundle, nudge, closing, tags: understoodTags(intent) };
  }

  /* ------------------------------------------------------------------ */
  /* Scent intelligence: families, key notes, accords, wear guide       */
  /* ------------------------------------------------------------------ */
  const NOTE_EMOJI = {
    vanilla: '🍦', caramel: '🍮', tonka: '🫘', praline: '🥜', honey: '🍯', chocolate: '🍫', coffee: '☕', sugar: '🍬', almond: '🌰', pistachio: '🥜', milk: '🥛',
    rose: '🌹', jasmine: '🌼', tuberose: '💮', peony: '🌸', lily: '🌷', 'orange blossom': '🌼', iris: '🪻', violet: '🪻', ylang: '🌺', gardenia: '🤍', magnolia: '🌸', freesia: '🌷', lavender: '🪻', orchid: '🌺', osmanthus: '🏵️', geranium: '🌺', narcissus: '🌼', 'cherry blossom': '🌸',
    oud: '🪵', sandalwood: '🪵', cedar: '🌲', vetiver: '🌾', patchouli: '🍂', guaiac: '🪵', cashmere: '🧣', pine: '🌲', birch: '🌳', oakmoss: '🌿',
    amber: '🟠', saffron: '🌾', incense: '🪔', benzoin: '🟤', cinnamon: '🥢', cardamom: '🫛', pepper: '🌶️', clove: '🔸', ginger: '🫚', tobacco: '🍂', leather: '🧳', smoke: '💨', rum: '🥃',
    bergamot: '🍋', lemon: '🍋', orange: '🍊', grapefruit: '🍊', citrus: '🍋', mint: '🌱', marine: '🌊', tea: '🍵', green: '🌿', basil: '🌿',
    apple: '🍏', pear: '🍐', peach: '🍑', plum: '🟣', cherry: '🍒', berries: '🫐', pineapple: '🍍', mango: '🥭', lychee: '🩷', coconut: '🥥', fig: '🍈', pomegranate: '🍎', melon: '🍈', 'passion fruit': '🥭',
    musk: '🤍', powder: '☁️', aldehydes: '🫧'
  };
  const FAMILY_DEF = {
    floral: { label: 'Floral', emoji: '🌸', color: '#D16B9E', notes: ['rose', 'jasmine', 'tuberose', 'peony', 'lily', 'orange blossom', 'ylang', 'gardenia', 'magnolia', 'freesia', 'orchid', 'osmanthus', 'geranium', 'narcissus', 'cherry blossom', 'violet', 'iris'] },
    fruity: { label: 'Fruity', emoji: '🍑', color: '#E8826A', notes: ['apple', 'pear', 'peach', 'plum', 'cherry', 'berries', 'pineapple', 'mango', 'lychee', 'fig', 'pomegranate', 'melon', 'passion fruit', 'coconut'] },
    gourmand: { label: 'Gourmand', emoji: '🍯', color: '#C98A3A', notes: ['vanilla', 'caramel', 'tonka', 'praline', 'honey', 'chocolate', 'coffee', 'sugar', 'almond', 'pistachio', 'milk', 'coconut'] },
    citrus: { label: 'Citrus', emoji: '🍋', color: '#E4C33B', notes: ['bergamot', 'lemon', 'orange', 'grapefruit', 'citrus'] },
    aquatic: { label: 'Aquatic', emoji: '🌊', color: '#3D8FC4', notes: ['marine'] },
    aromatic: { label: 'Aromatic', emoji: '🌿', color: '#5E9B5A', notes: ['lavender', 'mint', 'basil', 'tea', 'green'] },
    woody: { label: 'Woody', emoji: '🌲', color: '#8A6243', notes: ['sandalwood', 'cedar', 'vetiver', 'patchouli', 'guaiac', 'cashmere', 'pine', 'birch', 'oakmoss'] },
    oud: { label: 'Oud', emoji: '🪵', color: '#5B3A29', notes: ['oud'] },
    oriental: { label: 'Oriental', emoji: '🏮', color: '#B5562E', notes: ['amber', 'benzoin', 'incense', 'saffron', 'labdanum'] },
    spicy: { label: 'Spicy', emoji: '🌶️', color: '#C0392B', notes: ['saffron', 'cinnamon', 'cardamom', 'pepper', 'clove', 'ginger'] },
    musky: { label: 'Musky', emoji: '🤍', color: '#9B8BB8', notes: ['musk', 'aldehydes'] },
    powdery: { label: 'Powdery', emoji: '☁️', color: '#C9A6C9', notes: ['powder', 'iris', 'violet'] },
    leather: { label: 'Leather', emoji: '🧳', color: '#6B4A35', notes: ['leather', 'tobacco', 'rum'] },
    smoky: { label: 'Smoky', emoji: '🕯️', color: '#55456B', notes: ['smoke', 'incense', 'birch'] }
  };
  const FAMILY_WORDS = { fresh: 'citrus', aquatic: 'aquatic', oriental: 'oriental', gourmand: 'gourmand', floral: 'floral', woody: 'woody', fruity: 'fruity', spicy: 'spicy', musky: 'musky', powdery: 'powdery', leathery: 'leather', smoky: 'smoky', aromatic: 'aromatic', amber: 'oriental', ambery: 'oriental' };

  function noteEmoji(name) {
    const k = noteKey(name);
    if (k && NOTE_EMOJI[k]) return NOTE_EMOJI[k];
    const n = norm(name);
    if (/wood|bark|resin|cypriol|santo|agar/.test(n)) return '🪵';
    if (/flower|blossom|petal|bloom|lotus|lily/.test(n)) return '🌸';
    if (/fruit|berry|apricot|currant|date/.test(n)) return '🍑';
    if (/spice|nutmeg|pimento/.test(n)) return '🌶️';
    if (/moss|leaf|herb|grass|sage|rosemary|thyme/.test(n)) return '🌿';
    if (/sea|water|ozone|salt|rain/.test(n)) return '🌊';
    return '✨';
  }

  // Each note is classified into one or more families (weights), using a wide vocabulary.
  const NOTE_CLASS = [
    [/\boud|agar ?wood|aoud/, { oud: 1.3, woody: .4 }],
    [/orange blossom|neroli|petitgrain/, { floral: .8, citrus: .4 }],
    [/violet leaf/, { aromatic: 1 }],
    [/bergamot|lemon|lime\b|mandarin|tangerine|clementine|grapefruit|yuzu|citrus|citron|orange|bitter orange|blood orange|kumquat|pomelo|verbena/, { citrus: 1 }],
    [/apple|pear|peach|plum|cherry(?! blossom)|berr|black ?currant|cassis|pineapple|mango|lychee|litchi|fig|pomegranate|melon|passion|apricot|banana|grape|quince|guava|fruit|nectarine|kiwi|papaya|date\b|rhubarb|cranberr/, { fruity: 1 }],
    [/rose|jasmin|tuberose|peony|lily|ylang|gardenia|magnolia|freesia|orchid|osmanthus|geranium|narcissus|blossom|cyclamen|hyacinth|lotus|heliotrope|mimosa|lilac|carnation|frangipani|honeysuckle|floral|flower|petal|tiare|champaca|plumeria|marigold|davana|daffodil|muguet|lily of the valley|water lily|gardenia|camellia|wisteria|sambac|violet|iris|orris|tagetes|chamomile|linden|neroli/, { floral: 1 }],
    [/immortelle|helichrysum|hay\b|tobacco/, { oriental: .7, gourmand: .3 }],
    [/iris|orris|violet(?! leaf)|heliotrope|powder|rice|talc/, { powdery: .8 }],
    [/vanill|caramel|tonka|praline|honey|chocolate|cacao|cocoa|coffee|sugar|almond|pistachio|milk|cream|toffee|marshmallow|candy|biscuit|cookie|hazelnut|coumarin|coconut|dulce|meringue|cake|nougat|maple|butter|brown sugar|licorice|liquorice|whipped|chestnut|bubble ?gum|popcorn|tiramisu|macaron/, { gourmand: 1 }],
    [/rum|whisk|cognac|brandy|bourbon|wine|champagne|liqueur|boozy/, { gourmand: .6, leather: .3 }],
    [/marine|sea\b|aquatic|ozon|water|salt|calone|rain|seaweed|algae|sea ?spray|ocean|mineral|ambergris accord/, { aquatic: 1 }],
    [/lavender|lavandin|mint|basil|sage|rosemary|thyme|tea|green|grass|herb|artemisia|tarragon|eucalyptus|fern|fougere|galbanum|juniper|aromatic|bamboo|cucumber|petitgrain|cypress|shiso|mate|fresh|ivy|clary|tomato leaf|fig leaf|leaf|leaves/, { aromatic: 1 }],
    [/sandal|cedar|vetiver|patchouli|guaiac|cashmere|cashmeran|pine|birch|oakmoss|moss|wood|akigalawood|cypriol|nagarmotha|papyrus|ebony|teak|oak|hinoki|mahogany|rosewood|palo santo|haitian|virginia|atlas|iso e/, { woody: 1 }],
    [/amber|benzoin|frankincense|olibanum|incense|labdanum|myrrh|resin|styrax|opopanax|elemi|copal|balsam|tolu|peru|ambrox|ambroxan|ambrofix|cistus|oriental/, { oriental: 1 }],
    [/saffron|cinnamon|cardamom|pepper|clove|ginger|nutmeg|cumin|spice|spicy|anise|timur|pimento|coriander|chil+i|bay leaf|caraway|cassia|mace|allspice|szechuan|elemi/, { spicy: 1 }],
    [/musk|aldehyde|ambrette|clean|cotton|soap|linen|white flowers? musk|skin/, { musky: 1 }],
    [/leather|suede|castoreum|tobacco|tabac|hay/, { leather: 1 }],
    [/smok|tar\b|burnt|birch tar|cade|incense|guaiac/, { smoky: .7 }]
  ];
  function classifyNote(name) {
    const n = norm(name);
    const out = {};
    NOTE_CLASS.forEach(([re, w]) => { if (re.test(n)) Object.keys(w).forEach((f) => { out[f] = Math.max(out[f] || 0, w[f]); }); });
    return out;
  }
  const PAIR_ORDER = ['woody', 'floral', 'oriental', 'oud', 'gourmand', 'fruity', 'citrus', 'aromatic', 'aquatic', 'spicy', 'leather', 'musky', 'powdery', 'smoky'];

  const _famCache = new Map();
  function scentProfile(p) {
    const key = p.handle || p.title || p.desc;
    if (_famCache.has(key)) return _famCache.get(key);
    const info = productNotes(p);
    const score = {};
    const add = (fam, w) => { score[fam] = (score[fam] || 0) + w; };
    const tierW = { top: 0.9, heart: 1.3, base: 1.55 };
    let counted = 0;
    ['top', 'heart', 'base'].forEach((t) => info.tiers[t].forEach((raw, i) => {
      const c = classifyNote(raw);
      const pos = Math.pow(0.86, i);
      Object.keys(c).forEach((f) => add(f, c[f] * tierW[t] * pos));
      if (Object.keys(c).length) counted++;
    }));
    // without a notes section, read notes mentioned in the description (less weight)
    if (!counted) info.detected.forEach((k) => { const c = classifyNote(k); Object.keys(c).forEach((f) => add(f, c[f] * 0.8)); });
    const text = info.text;
    const ttl = norm(p.title || '');
    Object.keys(NOTES).forEach((k) => { if (NOTES[k].some((w) => hasWord(ttl, w))) { const c = classifyNote(k); Object.keys(c).forEach((f) => add(f, c[f] * 1.4)); } });
    Object.keys(FAMILY_WORDS).forEach((w) => { if (hasWord(text, w)) add(FAMILY_WORDS[w], counted ? 0.5 : 1.6); });
    if (score.oud) score.oud *= 1.35; // oud dominates whatever it touches
    const ranked = Object.keys(score).filter((f) => FAMILY_DEF[f]).sort((a, b) => score[b] - score[a]);
    let family = null;
    if (ranked.length && score[ranked[0]] >= 1) {
      const a = ranked[0], b = ranked[1];
      const pair = b && score[b] >= score[a] * 0.55;
      // name pairs the way perfumers do: "Woody Floral", "Floral Fruity", "Oriental Spicy"
      let first = a, second = pair ? b : null;
      if (second && PAIR_ORDER.indexOf(second) < PAIR_ORDER.indexOf(first) && score[second] >= score[first] * 0.9) { const t = first; first = second; second = t; }
      family = { key: a, label: FAMILY_DEF[first].label + (second ? ' ' + FAMILY_DEF[second].label : ''), emoji: FAMILY_DEF[a].emoji, color: FAMILY_DEF[a].color, second: second || null };
    }
    const top = ranked.length ? score[ranked[0]] : 1;
    const accords = ranked.slice(0, 6).map((f) => ({ key: f, label: FAMILY_DEF[f].label, emoji: FAMILY_DEF[f].emoji, color: FAMILY_DEF[f].color, pct: Math.round(Math.min(100, score[f] / top * 100)) }));
    // key notes: exactly one from each tier (top, heart, base), with the emoji written in the description
    const em = (n) => (info.emoji && info.emoji[String(n).toLowerCase()]) || noteEmoji(n);
    const keys = [];
    ['top', 'heart', 'base'].forEach((t) => { const n = (info.tiers[t] || [])[0]; if (n) keys.push({ tier: t, name: cap(n.toLowerCase()), emoji: em(n) }); });
    if (!keys.length) info.detected.slice(0, 3).forEach((k) => keys.push({ tier: '', name: cap(k), emoji: NOTE_EMOJI[k] || noteEmoji(k) }));
    // wear guide
    const warm = ['gourmand', 'oriental', 'oud', 'spicy', 'leather', 'smoky'].reduce((s, f) => s + (score[f] || 0), 0);
    const cool = ['citrus', 'aquatic', 'aromatic', 'fruity'].reduce((s, f) => s + (score[f] || 0), 0);
    const flo = score.floral || 0;
    const season = { spring: 30 + flo * 10 + cool * 6, summer: 20 + cool * 12 - warm * 4, autumn: 25 + warm * 8 + (score.woody || 0) * 6, winter: 15 + warm * 11 - cool * 4 };
    if (/summer/.test(text)) season.summer += 40; if (/spring/.test(text)) season.spring += 40;
    if (/autumn|fall\b/.test(text)) season.autumn += 40; if (/winter/.test(text)) season.winter += 40;
    const day = 50 + (cool + flo) * 7 - warm * 6 + (/\bday|office|daily|everyday/.test(text) ? 25 : 0);
    const night = 50 + warm * 8 - cool * 5 + (/evening|night|date/.test(text) ? 25 : 0);
    const norm100 = (o) => { const m = Math.max.apply(null, Object.values(o).map((v) => Math.max(v, 1))); Object.keys(o).forEach((k) => { o[k] = Math.max(8, Math.min(100, Math.round(o[k] / m * 100))); }); return o; };
    const res = { family, accords, keys, tiers: info.tiers, emoji: info.emoji || {}, structured: info.structured, season: norm100(season), time: norm100({ day: Math.max(day, 5), night: Math.max(night, 5) }), lasting: /long[- ]lasting|longevity|all[- ]day|beast/i.test(String(p.desc || '')) };
    _famCache.set(key, res);
    return res;
  }

  function catalogStats(items) {
    const notes = {}, vendors = {}, families = {};
    items.forEach((p) => {
      if (p.vendor) vendors[p.vendor] = (vendors[p.vendor] || 0) + 1;
      if (!isFragrance(p)) return;
      const pn = productNotes(p);
      const keys = new Set(pn.detected);
      pn.tiers.top.concat(pn.tiers.heart, pn.tiers.base).forEach((n) => { const k = noteKey(n); if (k) keys.add(k); });
      keys.forEach((k) => { notes[k] = (notes[k] || 0) + 1; });
      const prof = scentProfile(p);
      prof.accords.slice(0, 2).forEach((a) => { families[a.key] = (families[a.key] || 0) + 1; });
    });
    return { notes, vendors, families };
  }
  function loadCatalog(opts) {
    const cfg = Object.assign({ root: ((typeof window !== 'undefined' && window.theme && window.theme.routes && window.theme.routes.root) || '/').replace(/\/$/, ''), collections: { all: 'all' } }, opts || {});
    return Catalog.load(cfg);
  }

  const Engine = { scentProfile, tierParse, classifyNote, noteEmoji, noteKey, catalogStats, loadCatalog, isFragrance, NOTE_EMOJI, FAMILY_DEF, parseIntent, mergeIntent, productNotes, scoreProduct, rank, buildRequests, writeAdvice, respond, metaIntent, needsQuestion, findBundle, understoodTags, money, shortTitle, genderOf, NOTES, FAMILIES };

  if (typeof module !== 'undefined' && module.exports) { module.exports = Engine; return; }
  root.AmbientAIEngine = Engine;

  /* ------------------------------------------------------------------ */
  /* UI                                                                 */
  /* ------------------------------------------------------------------ */
  const $ = (s, c) => (c || document).querySelector(s);
  const $$ = (s, c) => Array.from((c || document).querySelectorAll(s));
  const modal = $('[data-ai]');
  if (!modal) return;
  const T = root.theme || { routes: {} };
  const cfgEl = $('[data-ai-config]', modal);
  const cfg = Object.assign({ limit: 8 }, cfgEl ? JSON.parse(cfgEl.textContent) : {});
  cfg.root = (T.routes.root || '/').replace(/\/$/, '');
  cfg.moneyFormat = T.moneyFormat;
  const sheet = $('.ai__sheet', modal);
  const thread = $('[data-ai-thread]', modal);
  const intro = $('[data-ai-intro]', modal);
  const form = $('[data-ai-form]', modal);
  const input = $('[data-ai-input]', modal);
  const status = $('[data-ai-status]', modal);
  const KEY = 'ambient:ai:v2';
  let state = { intent: null, last: [], pending: null, turn: 0, log: [] };
  let controller = null;
  let lastFocus = null;
  let busy = false;

  try { const saved = JSON.parse(sessionStorage.getItem(KEY)); if (saved && saved.log && saved.log.length) state = saved; } catch (e) { /* ignore */ }
  const save = () => { try { sessionStorage.setItem(KEY, JSON.stringify({ intent: state.intent, last: (state.last || []).slice(0, 10), pending: state.pending, turn: state.turn, log: state.log.slice(-14) })); } catch (e) { /* storage full or blocked */ } };

  const showIntro = (on) => { intro.hidden = !on; thread.hidden = on; sheet.classList.toggle('is-intro', on); };
  function restore() {
    if (!state.log.length) { showIntro(true); return; }
    showIntro(false);
    thread.innerHTML = '';
    state.log.forEach((e) => { if (e.role === 'user') addUser(e.text, true); else thread.insertAdjacentHTML('beforeend', e.html.replace('ai-msg--animate', '')); });
    requestAnimationFrame(() => { thread.scrollTop = thread.scrollHeight; });
  }

  function warmCatalog() {
    if (Catalog.items || Catalog.loading) return;
    Catalog.load(cfg).then((items) => {
      if (!items || !status) return;
      status.textContent = 'Reading all ' + items.length.toLocaleString('en-GB') + ' products';
      // pre-read notes in idle time so answers stay instant
      let i = 0;
      const idle = root.requestIdleCallback || ((fn) => setTimeout(() => fn({ timeRemaining: () => 8 }), 30));
      const step = (dl) => { while (i < items.length && dl.timeRemaining() > 2) productNotes(items[i++]); if (i < items.length) idle(step); };
      idle(step);
    });
  }

  function open(prefill) {
    lastFocus = document.activeElement;
    modal.hidden = false;
    document.body.classList.add('is-locked');
    requestAnimationFrame(() => modal.classList.add('is-open'));
    if (!modal.dataset.restored) { restore(); modal.dataset.restored = '1'; }
    warmCatalog();
    setTimeout(() => input.focus({ preventScroll: true }), 80);
    if (prefill) ask(prefill);
  }
  function close() {
    modal.classList.remove('is-open');
    document.body.classList.remove('is-locked');
    if (controller) controller.abort();
    setTimeout(() => { modal.hidden = true; }, 260);
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }
  function reset() {
    state = { intent: null, last: [], pending: null, turn: 0, log: [] };
    save();
    thread.innerHTML = '';
    showIntro(true);
    input.focus();
  }

  function addUser(text, silent) {
    const el = document.createElement('div');
    el.className = 'ai-msg ai-msg--user';
    el.textContent = text;
    thread.appendChild(el);
    if (!silent) el.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }
  function typing() {
    const el = document.createElement('div');
    el.className = 'ai-msg ai-msg--bot';
    el.innerHTML = '<span class="ai-avatar" aria-hidden="true"></span><div class="ai-typing" aria-label="Thinking"><span></span><span></span><span></span></div>';
    thread.appendChild(el);
    el.scrollIntoView({ block: 'end', behavior: 'smooth' });
    return el;
  }

  const svgPlus = '<svg class="icon icon--small" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke-linecap="round"/></svg>';
  const svgArrow = '<svg class="icon icon--small" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M4 12h15m-6-6 6 6-6 6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const img = (p, w) => p.img ? `<img src="${esc(p.img)}" alt="" loading="lazy" width="${w}" height="${w}">` : '';

  function cardHTML(x, i) {
    const p = x.p;
    const n = productNotes(p);
    const shown = uniq(x.matched.concat(n.tiers.top, n.tiers.heart, n.tiers.base, n.detected).map((s) => s.toLowerCase())).slice(0, 4);
    const save = x.onSale ? Math.round((1 - p.price / p.compare) * 100) : 0;
    const canAdd = p.available && p.v && p.single;
    return `<article class="ai-card${i === 0 ? ' ai-card--top' : ''}" role="listitem" style="--i:${i}">
      <span class="ai-card__rank">${i === 0 ? '<span class="badge badge--gold">Top pick</span>' : (save >= 5 ? '<span class="badge badge--sale">-' + save + '%</span>' : '')}${!p.available ? ' <span class="badge badge--muted">Sold out</span>' : ''}</span>
      <a href="${esc(p.url)}" class="ai-card__img" tabindex="-1">${img(p, 360)}</a>
      <div class="ai-card__body">
        <span class="ai-card__vendor">${esc(p.vendor)}</span>
        <a class="ai-card__title" href="${esc(p.url)}">${esc(p.title)}</a>
        ${shown.length ? '<div class="ai-card__notes">' + shown.map((s) => `<span${x.matched.includes(noteKey(s)) ? ' class="is-match"' : ''}>${esc(s)}</span>`).join('') + '</div>' : ''}
        <div class="ai-card__foot">
          <span class="ai-card__price">${money(p.price, cfg.moneyFormat)}${x.onSale ? '<s>' + money(p.compare, cfg.moneyFormat) + '</s>' : ''}</span>
          ${canAdd ? `<button type="button" class="ai-card__add" data-ai-add="${p.v}" aria-label="Add ${esc(p.title)} to bag">${svgPlus}</button>` : `<a class="ai-card__add" href="${esc(p.url)}" aria-label="View ${esc(p.title)}">${svgArrow}</a>`}
        </div>
      </div>
    </article>`;
  }

  function bundleHTML(b) {
    const why = b.reason === 'brand' ? 'Layer the matching ' + esc(b.b.type || 'body mist').toLowerCase() + ' over the perfume — it deepens the scent and makes it last even longer.' : 'They share key notes, so layering them makes your scent richer and longer-lasting.';
    const free = cfg.freeShipping && b.total >= cfg.freeShipping * 100 && b.a.price < cfg.freeShipping * 100;
    return `<div class="ai-bundle">
      <p class="ai-bundle__label">✨ Complete the ritual</p>
      <div class="ai-bundle__items">
        <a href="${esc(b.a.url)}">${img(b.a, 120)}<span>${esc(shortTitle(b.a))}</span></a>
        <span class="ai-bundle__plus" aria-hidden="true">+</span>
        <a href="${esc(b.b.url || (cfg.root + '/products/' + b.b.handle))}">${img(b.b, 120)}<span>${esc(shortTitle(b.b))}</span></a>
      </div>
      <p class="ai-bundle__why">${why}${free ? ' <strong>Together they unlock free delivery.</strong>' : ''}</p>
      <div class="ai-bundle__foot"><strong>${money(b.total, cfg.moneyFormat)}</strong><button type="button" class="btn btn--primary btn--small" data-ai-add-many="${b.a.v},${b.b.v}">Add both to bag</button></div>
    </div>`;
  }

  function renderBot(r) {
    const paras = (r.paragraphs || []).map((p, i) => `<p style="--d:${i}">${p}</p>`).join('');
    const tags = r.tags && r.tags.length ? '<div class="ai-understood" aria-label="What I understood">' + r.tags.map((x) => '<span>' + esc(x) + '</span>').join('') + '</div>' : '';
    const rail = r.products && r.products.length ? '<div class="ai-rail" role="list">' + r.products.map(cardHTML).join('') + '</div>' : '';
    const bundle = r.bundle ? bundleHTML(r.bundle) : '';
    let nudge = '';
    if (r.nudge && !r.bundle) nudge = `<p class="ai-nudge">🚚 You’re ${money(r.nudge.gap, cfg.moneyFormat)} away from free delivery${r.nudge.filler ? ` — <a href="${esc(r.nudge.filler.url || cfg.root + '/products/' + r.nudge.filler.handle)}">${esc(shortTitle(r.nudge.filler))}</a> (${money(r.nudge.filler.price, cfg.moneyFormat)}) would get you there.` : '.'}</p>`;
    const closing = r.closing ? `<p class="ai-closing">${esc(r.closing)}</p>` : '';
    const link = r.link ? `<a class="ai-chip ai-chip--gold" href="${esc(r.link.href)}">${esc(r.link.label)} →</a>` : '';
    const more = r.kind === 'answer' && r.products && r.products.length ? `<a class="ai-chip ai-chip--ghost" href="${esc(cfg.root + '/search?q=' + encodeURIComponent(r.intent.raw || '') + '&type=product')}">See all results →</a>` : '';
    const chips = (r.chips || []).map((c) => `<button type="button" class="ai-chip${r.kind === 'ask' ? ' ai-chip--big' : ''}" data-ai-ask="${esc(c)}">${esc(c)}</button>`).join('');
    return `<div class="ai-msg ai-msg--bot ai-msg--animate"><span class="ai-avatar" aria-hidden="true"></span><div class="ai-msg__body">
      <div class="ai-msg__bubble">${paras}${tags}</div>
      ${rail}${bundle}${nudge}${closing}
      <div class="ai-chips">${chips}${link}${more}</div>
    </div></div>`;
  }

  async function llmRewrite(q, r) {
    if (!cfg.endpoint || r.kind !== 'answer') return null;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 12000);
    try {
      const res = await fetch(cfg.endpoint, {
        method: 'POST', signal: ctrl.signal, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: q,
          history: state.log.filter((e) => e.role === 'user').slice(-6).map((e) => e.text),
          understood: r.tags,
          products: r.products.map((x) => ({ title: x.p.title, vendor: x.p.vendor, price: x.p.price / 100, compare_at: x.p.compare ? x.p.compare / 100 : null, available: x.p.available, url: x.p.url, notes: productNotes(x.p).tiers, description: String(x.p.desc).slice(0, 500) })),
          bundle: r.bundle ? { with: r.bundle.b.title, price: r.bundle.b.price / 100 } : null
        })
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data && data.answer ? String(data.answer).split(/\n{2,}/).map((p) => esc(p).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')) : null;
    } catch (e) { return null; } finally { clearTimeout(timer); }
  }

  async function ask(q) {
    q = String(q || '').trim();
    if (!q || busy) return;
    busy = true;
    if (controller) controller.abort();
    controller = new AbortController();
    showIntro(false);
    $$('.ai-msg--bot .ai-chips', thread).forEach((c) => c.classList.add('is-spent'));
    addUser(q);
    state.log.push({ role: 'user', text: q });
    input.value = '';
    const t0 = performance.now();
    const tEl = typing();
    modal.classList.add('is-thinking');
    let html;
    try {
      const r = await respond(q, state, cfg, { retrieve: (intent) => retrieve(intent, cfg, controller.signal) });
      const llm = await llmRewrite(q, r);
      if (llm) r.paragraphs = llm;
      html = renderBot(r);
    } catch (err) {
      if (err && err.name === 'AbortError') { tEl.remove(); modal.classList.remove('is-thinking'); busy = false; return; }
      html = renderBot({ kind: 'info', paragraphs: ['Sorry — I couldn’t reach the shelves just now. Could you try that again?'], chips: ['Best sellers', 'Under £30'] });
    }
    const wait = Math.max(0, 520 - (performance.now() - t0));
    await new Promise((r) => setTimeout(r, wait));
    tEl.remove();
    modal.classList.remove('is-thinking');
    thread.insertAdjacentHTML('beforeend', html);
    state.log.push({ role: 'bot', html });
    save();
    const msgs = $$('.ai-msg--user', thread);
    const lastUser = msgs[msgs.length - 1];
    if (lastUser) thread.scrollTo({ top: lastUser.offsetTop - 16, behavior: 'smooth' });
    busy = false;
  }

  async function addItems(ids, btn) {
    btn.classList.add('is-loading');
    try {
      const res = await fetch((T.routes.cartAdd || '/cart/add') + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: ids.map((id) => ({ id: +id, quantity: 1 })) }) });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || 'error');
      if (root.Ambient && root.Ambient.refreshCart) root.Ambient.refreshCart();
      if (root.Ambient && root.Ambient.toast) root.Ambient.toast(ids.length > 1 ? 'Both added to your bag ✓' : 'Added to your bag ✓');
      btn.classList.add('is-done');
      if (btn.classList.contains('ai-card__add')) btn.innerHTML = '<svg class="icon icon--small" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m5 12 5 5 9-10" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      else btn.textContent = 'Added ✓';
    } catch (e) {
      if (root.Ambient && root.Ambient.toast) root.Ambient.toast(e.message && e.message !== 'error' ? e.message : 'Could not add — please open the product.');
    } finally { btn.classList.remove('is-loading'); }
  }

  // Events
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-ai-open]');
    if (opener) { e.preventDefault(); open(opener.dataset.aiPrefill); return; }
    const asker = e.target.closest('[data-ai-ask]');
    if (asker) { e.preventDefault(); if (modal.hidden) open(); ask(asker.dataset.aiAsk); return; }
    if (e.target.closest('[data-ai-close]')) { e.preventDefault(); close(); return; }
    if (e.target.closest('[data-ai-reset]')) { e.preventDefault(); reset(); return; }
    const add = e.target.closest('[data-ai-add]');
    if (add) { e.preventDefault(); addItems([add.dataset.aiAdd], add); return; }
    const many = e.target.closest('[data-ai-add-many]');
    if (many) { e.preventDefault(); addItems(many.dataset.aiAddMany.split(','), many); }
  });
  form.addEventListener('submit', (e) => { e.preventDefault(); ask(input.value); });
  document.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); modal.hidden ? open() : close(); }
    else if (e.key === '/' && modal.hidden && !/input|textarea|select/i.test((document.activeElement || {}).tagName || '') && !(document.activeElement || {}).isContentEditable) { e.preventDefault(); open(); }
    else if (e.key === 'Escape' && !modal.hidden) close();
  });
  root.addEventListener('ambient:ai', (e) => open(e.detail && e.detail.q));

  // Voice input
  const SR = root.SpeechRecognition || root.webkitSpeechRecognition;
  const mic = $('[data-ai-mic]', modal);
  if (SR && mic) {
    mic.hidden = false;
    mic.addEventListener('click', () => {
      const rec = new SR(); rec.lang = 'en-GB'; rec.interimResults = true;
      mic.classList.add('is-listening');
      rec.onresult = (ev) => { input.value = Array.from(ev.results).map((r) => r[0].transcript).join(''); if (ev.results[ev.results.length - 1].isFinal) ask(input.value); };
      rec.onend = () => mic.classList.remove('is-listening');
      rec.start();
    });
  }

  // Animated example prompts in the search bars (pauses when hidden / modal open)
  $$('[data-ai-open][data-examples]').forEach((btn) => {
    const target = $('[data-ai-typing]', btn);
    const list = (btn.dataset.examples || '').split('|').map((s) => s.trim()).filter(Boolean);
    if (!target || !list.length || root.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const base = target.textContent;
    let i = 0, ch = 0, del = false, pause = 0;
    setInterval(() => {
      if (!modal.hidden || document.hidden) return;
      if (pause > 0) { pause--; return; }
      const word = list[i];
      if (!del) { ch++; target.textContent = '“' + word.slice(0, ch) + '”'; if (ch >= word.length) { del = true; pause = 22; } }
      else { ch -= 3; target.textContent = ch > 0 ? '“' + word.slice(0, ch) + '”' : base; if (ch <= 0) { del = false; ch = 0; i = (i + 1) % list.length; pause = 10; } }
    }, 70);
  });
})(typeof window !== 'undefined' ? window : globalThis);
